import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { callClaude, callClaudeJSON } from '../utils/claudeProxy';
import mammoth from 'mammoth';
import {
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc, query, where
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, getMetadata } from 'firebase/storage';
import {
  Box, Container, Typography, Button, Paper, TextField,
  IconButton, Alert, CircularProgress, Chip, LinearProgress,
  List, ListItem, ListItemText, ListItemSecondaryAction, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PeopleIcon from '@mui/icons-material/People';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SendIcon from '@mui/icons-material/Send';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Menu, MenuItem, Select, FormControl, InputLabel, InputAdornment, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  TENDER_VIEWS, inView, isClosed, statusesForView, loadView, saveView,
  formatDeadline, relativeDeadline, statusFromPhaseOutcomes,
} from '../utils/tenderListView';
import SearchIcon from '@mui/icons-material/Search';
import { deptCode, deptLabel } from '../utils/depts';
import { runTransaction } from 'firebase/firestore';
import CountdownClock from '../components/CountdownClock';
import DocumentPicker from '../components/DocumentPicker';
import TenderQA from '../components/TenderQA';
import TenderBidForm from '../components/TenderBidForm';
import TenderAlarms from '../components/TenderAlarms';
import ReferencePicker from '../components/ReferencePicker';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import BadgeIcon from '@mui/icons-material/Badge';
import PreviewIcon from '@mui/icons-material/Preview';

// Rollen i anbudet. Som underkonsult lämnar en samarbetspartner in anbudet och
// vi tar bara fram en delmängd av handlingarna.
const BID_ROLES = {
  prime:         { label: 'Vi lämnar in anbudet', short: 'Eget anbud' },
  subconsultant: { label: 'Vi är underkonsult åt annan', short: 'Underkonsult' },
};
const SUBCONSULTANT_COLOR = '#00695C';

const isSub = tender => tender?.bidRole === 'subconsultant';

// Anbudslistans kolumner. Delas av rubrikraden och varje anbudsrad så att
// värdena alltid står på samma ställe.
const LIST_GRID = 'minmax(0,1fr) 116px 108px 56px 200px 150px 68px 68px 32px';
const LIST_GAP = '12px';
const LIST_MIN_WIDTH = 1080;
const STATUS_PILL = {
  draft:     { bg: '#9E9E9E', label: 'Utkast' },
  active:    { bg: '#FF6B00', label: 'Aktivt' },
  submitted: { bg: '#1565C0', label: 'Inlämnat' },
  won:       { bg: '#2E7D32', label: 'Vunnet' },
  lost:      { bg: '#C62828', label: 'Förlorat' },
  declined:  { bg: '#757575', label: 'Avvisat' },
  withdrawn: { bg: '#BDBDBD', label: 'Återdraget' },
};
const STATUS_HINT = {
  declined:  'Vi lämnar inte in – historiken sparas',
  withdrawn: 'Inlämnat men återkallat',
};
const DEADLINE_TONE = { passed: '#C62828', soon: '#B26A00', normal: 'text.secondary' };
const CLOSED_TEXT = '#9E9E9E';

// Handlingarnas ägarskap nycklas på texten, inte på index. Avbockningarna är
// indexbaserade och pekar på fel rader så fort listan ändras — den svagheten
// ska inte byggas vidare på.
const docKey = t => String(t || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Handlingar som är våra att ta fram. För eget anbud är alla våra.
function ourDocuments(tender, phase) {
  const all = phase?.requiredDocuments || [];
  if (!isSub(tender)) return all;
  const ours = new Set(phase?.ourDocuments || []);
  return all.filter(d => ours.has(docKey(d)));
}

// Stabil nyckel per fil. Radering och godkännande skedde tidigare på url, vilket
// slutar fungera så fort en fil har flera versioner.
const newFileId = () => 'f_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

// Filer uppladdade före versionshanteringen saknar id, version och godkännande.
// De läses som v1 utan känd uppladdare i stället för att databasen migreras.
function fileMeta(file) {
  return {
    ...file,
    id: file.id || file.url,
    version: file.version || 1,
    uploadedByName: file.uploadedByName || '',
    approval: file.approval || { status: 'pending' },
    versions: file.versions || [],
    contributor: file.contributor || { type: 'internal' },
  };
}

function formatStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('sv-SE',
    { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Grupperingsnyckel: våra egna handlingar i en grupp, varje underkonsult i en egen.
const contribKey = c => c?.type === 'subcontractor'
  ? 'sub_' + (c.memberId || c.name || c.company || 'okand')
  : 'internal';

const contribLabel = c => c?.type === 'subcontractor'
  ? ([c.company, c.role].filter(Boolean).join(' \u00b7 ') || c.name || 'Underkonsult')
  : 'V\u00e5ra handlingar';

const isApproved = f => fileMeta(f).approval?.status === 'approved';

// Slår ihop en befintlig checklista med AI:ns nya förslag. Befintliga poster
// behåller sin plats och sin bock — även de som lagts till för hand — och bara
// det som verkligen är nytt läggs till sist. Utan detta nollställer en ny analys
// allt avbockat arbete, och eftersom bockarna är index skulle de dessutom peka
// på fel rader så fort listan ändrar innehåll.
function mergeChecklist(existingItems, existingChecked, incomingItems) {
  const existing = existingItems || [];
  const checked = existingChecked || [];
  const key = t => String(t).trim().toLowerCase().replace(/\s+/g, ' ');
  const seen = new Set(existing.map(key));
  const added = (incomingItems || []).filter(t => {
    const k = key(t);
    if (!t?.trim() || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return {
    items: [...existing, ...added],
    // Indexen är oförändrade eftersom de befintliga posterna ligger kvar först.
    checked: checked.filter(i => i < existing.length),
  };
}

// Schema för AI-analysen av förfrågningsunderlaget. Fälten är obligatoriska —
// utan det hoppar modellen över de långa fritextfälten när svaret börjar bli stort,
// och då försvinner just sammanfattningen och projektorienteringen.
const STR = { type: 'string' };
const STR_LIST = { type: 'array', items: { type: 'string' } };

// Svar från AI-analysen av utvärderingsdokument. documentMatchesTender fångar
// det vanliga misstaget att fel upphandlings tilldelningsbeslut laddats upp.
const EVAL_SCHEMA = {
  type: 'object',
  properties: {
    documentMatchesTender: { type: 'boolean', description: 'true om dokumenten gäller just det här anbudet/upphandlingen.' },
    documentReference: { ...STR, description: 'Vilken upphandling dokumenten gäller: namn och diarienummer.' },
    result: { type: 'string', enum: ['won', 'lost', 'unknown'] },
    ourScore: { ...STR, description: 'Vårt totalbetyg om angivet, annars tom sträng.' },
    winnerScore: { ...STR, description: 'Vinnarens betyg om angivet, annars tom sträng.' },
    ourPrice: STR,
    winnerPrice: STR,
    priceAnalysis: { ...STR, description: 'Analys av prisbilden – var vi för dyra, billiga, lagom?' },
    qualityAnalysis: { ...STR, description: 'Hur bedömdes vi på kompetens, metod, genomförande?' },
    mainReasons: { ...STR_LIST, description: 'Huvudorsaker till utfallet.' },
    improvements: { ...STR_LIST, description: 'Konkreta förbättringsområden.' },
    summary: { ...STR, description: 'Sammanfattning på 3–5 meningar – vad kan vi lära oss?' },
  },
  required: ['documentMatchesTender', 'documentReference', 'result', 'ourScore', 'winnerScore',
    'ourPrice', 'winnerPrice', 'priceAnalysis', 'qualityAnalysis', 'mainReasons', 'improvements', 'summary'],
  additionalProperties: false,
};

const BRIEFING_SCHEMA = {
  type: 'object',
  properties: {
    briefingText: {
      type: 'string',
      description: 'Kort sammanfattning av vad upphandlingen efterfrågar, 4–6 meningar. '
        + 'Ska gå att läsa fristående och ge en direkt förståelse för uppdraget.',
    },
    projectOrientation: {
      type: 'string',
      description: 'Kort orientering om det aktuella projektet, 3–5 meningar: vad som ska '
        + 'byggas eller utföras, var, omfattning och tidplan. Tom sträng om underlaget inte beskriver något projekt.',
    },
    requiredRoles:      { ...STR_LIST, description: 'Efterfrågade roller och befattningar.' },
    requiredSkills:     { ...STR_LIST, description: 'Efterfrågade kompetenser och erfarenheter.' },
    keywords:           { ...STR_LIST, description: 'Viktiga nyckelord.' },
    mustRequirements:   { ...STR_LIST, description: 'SKA-krav (obligatoriska krav).' },
    shouldRequirements: { ...STR_LIST, description: 'BÖR-krav (mervärdeskrav).' },
    requiredDocuments:  { ...STR_LIST, description: 'Handlingar som ska bifogas anbudet.' },
    projectType:        { ...STR, description: 'Typ av projekt.' },
    duration:           { ...STR, description: 'Uppskattad uppdragslängd om angiven, annars tom sträng.' },
    contractValue:      { ...STR, description: 'Kontraktsvärde om angivet, annars tom sträng.' },
    submissionDeadline: { ...STR, description: 'Inlämningsdeadline som YYYY-MM-DD HH:MM, annars tom sträng.' },
    portalUrl:          { ...STR, description: 'URL till anbudsportal om den finns i underlaget, annars tom sträng.' },
    notes:              { ...STR, description: 'Övrigt viktigt att känna till.' },
  },
  required: ['briefingText', 'projectOrientation', 'requiredRoles', 'requiredSkills',
    'keywords', 'mustRequirements', 'shouldRequirements', 'requiredDocuments',
    'projectType', 'duration', 'contractValue', 'submissionDeadline', 'portalUrl', 'notes'],
  additionalProperties: false,
};

// ownership (valfritt): { isOurs(item), onToggle(index) } — märker varje rad som
// vår eller partnerns när vi är underkonsult. Rader som inte är våra går inte
// att bocka av; de ligger kvar som information om vad upphandlingen kräver.
function EditableChecklist({ items, checkedItems, onToggle, onAdd, onRemove,
  labelColor, emptyLabel, ownership }) {
  const [input, setInput] = useState('');
  return (
    <Box>
      {items.map((r, i) => {
        const checked = checkedItems?.includes(i);
        const ours = ownership ? ownership.isOurs(r) : true;
        return (
          <Box key={i} sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.3,
            borderRadius: 1, opacity: ours ? 1 : 0.5,
            '&:hover .delete-btn': { opacity: 1 }
          }}>
            <Box onClick={() => ours && onToggle(i)} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              cursor: ours ? 'pointer' : 'default', flex: 1,
              '&:hover': { bgcolor: ours ? 'action.hover' : 'transparent' },
              borderRadius: 1, py: 0.2
            }}>
              {checked
                ? <CheckBoxIcon sx={{ fontSize: 18, color: '#2e7d32', flexShrink: 0 }} />
                : <CheckBoxOutlineBlankIcon sx={{ fontSize: 18,
                    color: labelColor || 'text.secondary', flexShrink: 0 }} />}
              <Typography variant="body2" sx={{
                textDecoration: checked ? 'line-through' : 'none',
                color: checked ? 'text.secondary' : labelColor || 'text.primary'
              }}>
                {r}
              </Typography>
            </Box>
            {ownership && (
              <Tooltip title={ours
                ? 'Vi tar fram den här handlingen — klicka för att lämna den till partnern'
                : 'Partnerns handling — klicka för att ta den själva'}>
                <Chip size="small" label={ours ? 'VÅR' : 'Partnerns'}
                  onClick={() => ownership.onToggle(i)}
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: ours ? 700 : 400,
                    cursor: 'pointer', flexShrink: 0,
                    bgcolor: ours ? SUBCONSULTANT_COLOR : 'transparent',
                    color: ours ? 'white' : 'text.secondary',
                    border: ours ? 'none' : '1px solid',
                    borderColor: 'divider' }} />
              </Tooltip>
            )}
            <IconButton size="small" className="delete-btn"
              onClick={() => onRemove(i)}
              sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.3 }}>
              <DeleteIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          </Box>
        );
      })}
      {items.length === 0 && (
        <Typography variant="caption" color="text.secondary"
          sx={{ fontStyle: 'italic' }}>
          {emptyLabel || 'Inga poster ännu'}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <TextField size="small" fullWidth placeholder="Lägg till..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && input.trim()) {
              onAdd(input.trim()); setInput('');
            }
          }}
          sx={{ '& .MuiInputBase-input': { py: 0.7, fontSize: '0.85rem' } }}
        />
        <IconButton size="small" disabled={!input.trim()}
          onClick={() => { onAdd(input.trim()); setInput(''); }}
          sx={{ border: '1px solid', borderColor: 'divider' }}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

/** Millisekunder ur createdAt, som kan vara Timestamp, Date eller sträng. */
function createdMs(tender) {
  const v = tender.createdAt;
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

/**
 * Nyaste anbud överst. Anbud som skapades innan createdAt fanns hamnar sist
 * och sorteras sinsemellan på anbudsnumret, som är löpande per avdelning.
 */
function byNewestFirst(a, b) {
  const am = createdMs(a);
  const bm = createdMs(b);
  if (am != null && bm != null && am !== bm) return bm - am;
  if (am != null && bm == null) return -1;
  if (am == null && bm != null) return 1;
  return (b.tenderNumber || '').localeCompare(a.tenderNumber || '', 'sv', { numeric: true });
}

export default function Tenders() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenders, setTenders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [aggregating, setAggregating] = useState(false);
  const [aggregateResult, setAggregateResult] = useState('');
  const [aggregateDialog, setAggregateDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitProgress, setSubmitProgress] = useState({});
  const [evalProgress, setEvalProgress] = useState({});
  const [confirmProgress, setConfirmProgress] = useState({});
  const [dialog, setDialog] = useState({ open: false, editId: '' });
  const [pickerTenderInfo, setPickerTenderInfo] = useState(null); // {tenderId, phaseIdx}
  const [refPickerTenderId, setRefPickerTenderId] = useState(null);
  const [activePhaseIdx, setActivePhaseIdx] = useState({});
  const [collapsed, setCollapsed] = useState({});
  // Statusmenyn som öppnas från statusetiketten i listan.
  const [statusMenu, setStatusMenu] = useState(null); // { anchor, tender }
  const [collapsedRefs, setCollapsedRefs] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [allUsers, setAllUsers] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [crmCompanies, setCrmCompanies] = useState([]);
  // Analysbesked per anbud. Sidans gemensamma ruta ligger högst upp och hamnar
  // utanför bild när man står i ett kort långt ned i listan — beskedet måste
  // synas där knappen finns.
  const [analysisMsg, setAnalysisMsg] = useState({});
  // Filstorlekar per nedladdnings-URL. Storage-metadata i stället för HEAD —
  // bucketens CORS-regler tillåter inte HEAD.
  const [fileSizes, setFileSizes] = useState({});
  // Vilka FU-filer som ska analyseras, per anbud+fas. Saknas nyckeln är alla valda.
  const [analyzeSel, setAnalyzeSel] = useState({});
  // Uppladdningsdialogen: vem filerna kommer från och vad som ska hända med
  // namn som redan finns.
  const [uploadQueue, setUploadQueue] = useState(null);
  const [openHistory, setOpenHistory] = useState({});
  const [filterRole, setFilterRole] = useState('');
  const [teamByTender, setTeamByTender] = useState({});
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Pågående | Avslutade | Alla. Sparas per användare i webbläsaren.
  const [view, setView] = useState(() => loadView(currentUser?.uid));
  const [groupByDept, setGroupByDept] = useState(false);
  const [form, setForm] = useState({
    name: '', client: '', deadline: '', briefingText: '',
    briefingFiles: [], requiredRoles: [], requiredSkills: [],
    keywords: [], mustRequirements: [], shouldRequirements: [],
    requiredDocuments: [], status: 'draft', departmentId: '',
    portalUrl: '', submissionDeadline: '',
    bidRole: 'prime', partnerCompanyId: '', partnerName: '',
    partnerContactName: '', partnerContactEmail: '', partnerContactPhone: '',
    partnerDeadline: ''
  });

  useEffect(() => {
    loadTenders().then(() => {
      if (searchParams.get('new') === '1') setDialog({ open: true, editId: '' });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  async function loadTenders() {
    setLoading(true);
    const [tenderSnap, deptSnap, userSnap] = await Promise.all([
      getDocs(collection(db, 'tenders')),
      getDocs(collection(db, 'departments')),
      getDoc(doc(db, 'users', currentUser.uid)),
    ]);
    const allTenders = tenderSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const userData = userSnap.exists() ? userSnap.data() : {};
    setUserProfile(userData);
    let filtered = allTenders;
    if (userData.role === 'medarbetare' || userData.isAnbudsansvarig) {
      filtered = allTenders.filter(t => t.departmentId === userData.departmentId);
    } else if (userData.isAdmin) {
      filtered = allTenders.filter(t => t.companyId === userData.companyId);
    }
    setTenders(filtered);
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
    getDocs(collection(db, 'users')).then(snap =>
      setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    getDocs(collection(db, 'crmCompanies')).then(snap =>
      setCrmCompanies(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }

  async function loadTeam(tenderId) {
    if (teamByTender[tenderId] !== undefined) return;
    setTeamByTender(prev => ({ ...prev, [tenderId]: null }));
    // Hämtar alla medlemmar, inte bara bekräftade — underkonsultbehoven har
    // status 'needed' och behövs för att kunna märka upp filer med avsändare.
    const snap = await getDocs(
      query(collection(db, 'tenderMembers'),
        where('tenderId', '==', tenderId))
    );
    setTeamByTender(prev => ({
      ...prev,
      [tenderId]: snap.docs.map(d => ({ id: d.id, ...d.data() }))
    }));
  }

  function toggleCollapse(id) {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  }
  function collapseAll() {
    const all = {};
    tenders.forEach(t => { all[t.id] = true; });
    setCollapsed(all);
  }
  function expandAll() { setCollapsed({}); }
  function focusOn(id) {
    const all = {};
    tenders.forEach(t => { all[t.id] = t.id !== id; });
    setCollapsed(all);
  }

  function toggleCollapseRef(refId) {
    setCollapsedRefs(prev => ({ ...prev, [refId]: !prev[refId] }));
  }
  function collapseAllRefs(refs) {
    const all = {};
    (refs || []).forEach(r => { all[r.refId] = true; });
    setCollapsedRefs(prev => ({ ...prev, ...all }));
  }
  function expandAllRefs(refs) {
    const cleared = {};
    (refs || []).forEach(r => { cleared[r.refId] = false; });
    setCollapsedRefs(prev => ({ ...prev, ...cleared }));
  }
  function focusOnRef(focusId, refs) {
    const all = {};
    (refs || []).forEach(r => { all[r.refId] = r.refId !== focusId; });
    setCollapsedRefs(prev => ({ ...prev, ...all }));
  }

  async function generateTenderNumber(departmentId) {
    const dept = departments.find(d => d.id === departmentId && d.offerCode);
    if (!dept) return '';
    const year = new Date().getFullYear();
    const yy = String(year).slice(-2);
    const counterId = `${dept.id}_${year}`;
    const counterRef = doc(db, 'offerCounters', counterId);
    const newCount = await runTransaction(db, async tx => {
      const snap = await tx.get(counterRef);
      const startAt = dept.offerStartNumber || 1;
      const current = snap.exists() ? snap.data().count : startAt - 1;
      const next = current + 1;
      tx.set(counterRef, { count: next, deptId: dept.id, year });
      return next;
    });
    return `${dept.offerCode}-${yy}-${String(newCount).padStart(3, '0')}`;
  }

  function openNew() {
    setForm({
      name: '', client: '', deadline: '', briefingText: '',
      briefingFiles: [], requiredRoles: [], requiredSkills: [],
      keywords: [], mustRequirements: [], shouldRequirements: [],
      requiredDocuments: [], status: 'draft', departmentId: '',
      portalUrl: '', submissionDeadline: '',
      bidRole: 'prime', partnerCompanyId: '', partnerName: '',
      partnerContactName: '', partnerContactEmail: '', partnerContactPhone: '',
      partnerDeadline: ''
    });
    setDialog({ open: true, editId: '' });
  }

  function openEdit(tender) {
    setForm({
      name: tender.name || '',
      client: tender.client || '',
      deadline: tender.deadline || '',
      briefingText: tender.briefingText || '',
      briefingFiles: tender.briefingFiles || [],
      requiredRoles: tender.requiredRoles || [],
      requiredSkills: tender.requiredSkills || [],
      keywords: tender.keywords || [],
      mustRequirements: tender.mustRequirements || [],
      shouldRequirements: tender.shouldRequirements || [],
      requiredDocuments: tender.requiredDocuments || [],
      status: tender.status || 'draft',
      departmentId: tender.departmentId || '',
      portalUrl: tender.portalUrl || '',
      submissionDeadline: tender.submissionDeadline || '',
      bidRole: tender.bidRole || 'prime',
      partnerCompanyId: tender.partnerCompanyId || '',
      partnerName: tender.partnerName || '',
      partnerContactName: tender.partnerContactName || '',
      partnerContactEmail: tender.partnerContactEmail || '',
      partnerContactPhone: tender.partnerContactPhone || '',
      partnerDeadline: tender.partnerDeadline || ''
    });
    setDialog({ open: true, editId: tender.id });
  }

  // Superadmin, VD och admin får sätta vilken status som helst. Övriga måste
  // ladda upp en bekräftelse innan ett anbud får markeras som inlämnat.
  const isSuperadmin = userProfile?.role === 'superadmin' || userProfile?.role === 'vd';
  const canOverrideStatus = isSuperadmin || userProfile?.isAdmin === true;

  // Bekräftelsefiler ligger på fasen för anbud med faser och på anbudet självt
  // för äldre anbud utan faser — leta på båda ställena.
  function hasConfirmation(tender) {
    if (!tender) return false;
    if (tender.confirmFiles?.length > 0) return true;
    return (tender.phases || []).some(p => p.confirmFiles?.length > 0);
  }

  // Snabbyte av status direkt från listan – samma regler som i redigeringen.
  async function handleQuickStatus(tender, status) {
    setStatusMenu(null);
    if (status === tender.status) return;
    await updateDoc(doc(db, 'tenders', tender.id), { status, updatedAt: new Date() });
    setTenders(prev => prev.map(t => (t.id === tender.id ? { ...t, status } : t)));
    setSaved(`Status ändrad till ${STATUS_PILL[status].label}`);
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    const dept = departments.find(d => d.id === form.departmentId);
    const data = { ...form, companyId: dept?.companyId || '', updatedAt: new Date() };
    if (dialog.editId) {
      await updateDoc(doc(db, 'tenders', dialog.editId), data);
    } else {
      const tenderNumber = await generateTenderNumber(form.departmentId);
      await addDoc(collection(db, 'tenders'), {
        ...data, tenderNumber, createdBy: currentUser.uid, createdAt: new Date()
      });
    }
    setSaved('Anbud sparat!');
    setTimeout(() => setSaved(''), 2000);
    setDialog({ open: false, editId: '' });
    loadTenders();
  }

  async function handleDelete(id) {
    await deleteDoc(doc(db, 'tenders', id));
    loadTenders();
  }

  // outcome: 'won' | 'lost' | null (null = återställ). Fasens utfall och
  // anbudets status skrivs i samma uppdatering så att de aldrig glider isär –
  // statusen styr listan, statistiken och vyfiltret.
  async function handleSetPhaseOutcome(tenderId, phaseIdx, outcome) {
    const snap = await getDoc(doc(db, 'tenders', tenderId));
    const data = snap.data() || {};
    let updates;
    if (data.phases?.length) {
      const phases = data.phases.map((p, i) => (i === phaseIdx ? { ...p, outcome: outcome || null } : p));
      updates = { phases, status: statusFromPhaseOutcomes(phases, data.status) };
    } else {
      updates = { outcome: outcome || null, status: outcome || 'submitted' };
    }
    await updateDoc(doc(db, 'tenders', tenderId), updates);
    setTenders(prev => prev.map(x => (x.id === tenderId ? { ...x, ...updates } : x)));
  }

  // ── Phase helpers ────────────────────────────────────────────────────────────

  function getPhaseIdx(tenderId) {
    return activePhaseIdx[tenderId] || 0;
  }

  // Reads the tender fresh from Firestore, applies updates to the active phase
  // (or directly to the tender doc for legacy tenders without phases).
  async function phaseUpdate(tenderId, phaseIdx, getUpdates) {
    const snap = await getDoc(doc(db, 'tenders', tenderId));
    const data = snap.data();
    if (data?.phases?.length > 0) {
      const phase = data.phases[phaseIdx] || data.phases[0];
      const updates = getUpdates(phase);
      const newPhases = data.phases.map((p, i) => i === phaseIdx ? { ...p, ...updates } : p);
      await updateDoc(doc(db, 'tenders', tenderId), { phases: newPhases });
      setTenders(prev => prev.map(t => t.id === tenderId ? { ...t, phases: newPhases } : t));
    } else {
      const updates = getUpdates(data);
      await updateDoc(doc(db, 'tenders', tenderId), updates);
      setTenders(prev => prev.map(t => t.id === tenderId ? { ...t, ...updates } : t));
    }
  }

  async function handleAddPhase(tender) {
    const existing = tender.phases;
    const emptyPhase = {
      id: 'phase_' + Date.now(),
      name: `Fas ${(existing?.length || 1) + 1}`,
      submissionDeadline: '', portalUrl: '', briefingText: '', projectOrientation: '',
      briefingFiles: [], submitFiles: [], libraryDocuments: [],
      mustRequirements: [], mustRequirementsChecked: [],
      shouldRequirements: [], shouldRequirementsChecked: [],
      requiredDocuments: [], requiredDocumentsChecked: [],
      confirmFiles: [],
    };
    if (!existing || existing.length === 0) {
      // Migrate flat fields → phase 0, add new phase 1
      const phase0 = {
        id: 'phase_0', name: 'Fas 1',
        submissionDeadline: tender.submissionDeadline || '',
        portalUrl: tender.portalUrl || '',
        briefingText: tender.briefingText || '',
        projectOrientation: tender.projectOrientation || '',
        briefingFiles: tender.briefingFiles || [],
        submitFiles: tender.submitFiles || [],
        libraryDocuments: tender.libraryDocuments || [],
        requiredRoles: tender.requiredRoles || [],
        requiredSkills: tender.requiredSkills || [],
        keywords: tender.keywords || [],
        mustRequirements: tender.mustRequirements || [],
        mustRequirementsChecked: tender.mustRequirementsChecked || [],
        shouldRequirements: tender.shouldRequirements || [],
        shouldRequirementsChecked: tender.shouldRequirementsChecked || [],
        requiredDocuments: tender.requiredDocuments || [],
        requiredDocumentsChecked: tender.requiredDocumentsChecked || [],
        confirmFiles: tender.confirmFiles || [],
      };
      const newPhases = [phase0, emptyPhase];
      await updateDoc(doc(db, 'tenders', tender.id), { phases: newPhases });
      setTenders(prev => prev.map(t => t.id === tender.id ? { ...t, phases: newPhases } : t));
      // Stay on Fas 1 so user sees their existing data (new Fas 2 chip is clickable)
    } else {
      const newPhases = [...existing, emptyPhase];
      await updateDoc(doc(db, 'tenders', tender.id), { phases: newPhases });
      setTenders(prev => prev.map(t => t.id === tender.id ? { ...t, phases: newPhases } : t));
      setActivePhaseIdx(prev => ({ ...prev, [tender.id]: newPhases.length - 1 }));
    }
  }

  // ── Phase-aware list/check handlers ─────────────────────────────────────────

  async function handleToggleCheck(tenderId, field, index, phaseIdx) {
    await phaseUpdate(tenderId, phaseIdx, phase => {
      const checkedItems = phase?.[field + 'Checked'] || [];
      const newChecked = checkedItems.includes(index)
        ? checkedItems.filter(i => i !== index)
        : [...checkedItems, index];
      return { [field + 'Checked']: newChecked };
    });
  }

  async function handleAddListItem(tenderId, field, value, currentList, phaseIdx) {
    const newList = [...(currentList || []), value];
    await phaseUpdate(tenderId, phaseIdx, () => ({ [field]: newList }));
  }

  async function handleRemoveListItem(tenderId, field, index, currentList, phaseIdx) {
    const newList = (currentList || []).filter((_, i) => i !== index);
    await phaseUpdate(tenderId, phaseIdx, phase => {
      const checkedField = field + 'Checked';
      const checked = phase?.[checkedField] || [];
      const newChecked = checked
        .filter(i => i !== index)
        .map(i => i > index ? i - 1 : i);
      return { [field]: newList, [checkedField]: newChecked };
    });
  }

  async function handleAttachLibraryDocs(tenderId, docs, phaseIdx) {
    await phaseUpdate(tenderId, phaseIdx, () => ({ libraryDocuments: docs }));
    setSaved('Dokument bifogade!');
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleAttachReferenceProjects(tenderId, refs) {
    await updateDoc(doc(db, 'tenders', tenderId), { tenderReferenceProjects: refs });
    setTenders(prev => prev.map(t =>
      t.id === tenderId ? { ...t, tenderReferenceProjects: refs } : t
    ));
  }

  async function handleRemoveRefProject(tenderId, refId, currentRefs) {
    const updated = (currentRefs || []).filter(r => r.refId !== refId);
    await updateDoc(doc(db, 'tenders', tenderId), { tenderReferenceProjects: updated });
    setTenders(prev => prev.map(t =>
      t.id === tenderId ? { ...t, tenderReferenceProjects: updated } : t
    ));
  }

  async function handleUploadFile(e, tenderId, phaseIdx) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const file of files) {
      const fileKey = tenderId + '_' + file.name;
      setUploadProgress(prev => ({ ...prev, [fileKey]: 1 }));
      const storageRef = ref(storage,
        'tenders/' + tenderId + '/p' + phaseIdx + '/' + Date.now() + '_' + file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          snap => {
            const pct = Math.round(snap.bytesTransferred / snap.totalBytes * 100);
            setUploadProgress(prev => ({ ...prev, [fileKey]: pct }));
          },
          err => { reject(err); },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            await phaseUpdate(tenderId, phaseIdx, phase => {
              const existing = phase?.briefingFiles || [];
              return { briefingFiles: [...existing, { name: file.name, url, size: file.size, uploadedAt: new Date().toISOString() }] };
            });
            setUploadProgress(prev => { const n = { ...prev }; delete n[fileKey]; return n; });
            resolve();
          }
        );
      });
    }
    setSaved('Filer uppladdade!');
    setTimeout(() => setSaved(''), 2000);
  }

  // Öppnar dialogen där avsändare väljs och namnkrockar reds ut. Själva
  // uppladdningen sker först när valen är gjorda.
  function handleUploadSubmitFile(e, tenderId, phaseIdx) {
    const picked = Array.from(e.target.files);
    e.target.value = '';
    if (!picked.length) return;
    const tender = tenders.find(t => t.id === tenderId);
    const phase = tender?.phases?.[phaseIdx] ?? tender;
    const existing = (phase?.submitFiles || []).map(fileMeta);
    setUploadQueue({
      tenderId, phaseIdx,
      contributorKey: 'internal',
      items: picked.map(file => {
        const match = existing.find(f => f.name.toLowerCase() === file.name.toLowerCase());
        // matchId behålls så att valet kan ändras fram och tillbaka i dialogen.
        return { file, name: file.name,
          matchId: match ? match.id : '', replaceId: match ? match.id : '',
          matchName: match?.name || '', matchVersion: match?.version || 0 };
      }),
    });
  }

  async function runSubmitUpload(queue) {
    const { tenderId, phaseIdx, items, contributorKey: ck } = queue;
    const members = teamByTender[tenderId] || [];
    const need = members.find(m => m.id === ck.replace(/^sub_/, ''));
    const contributor = ck === 'internal'
      ? { type: 'internal' }
      : {
          type: 'subcontractor',
          memberId: need?.id || '',
          name: need?.assignedName || '',
          company: need?.assignedCompany || '',
          role: need?.suggestedRole || '',
        };
    const me = allUsers.find(u => u.id === currentUser.uid);
    const myName = me?.name || me?.displayName || me?.email || 'Okänd';
    setUploadQueue(null);

    for (const item of items) {
      const file = item.file;
      const fileKey = 'submit_' + tenderId + '_' + file.name;
      setSubmitProgress(prev => ({ ...prev, [fileKey]: 1 }));
      const storageRef = ref(storage,
        'tenders/' + tenderId + '/submit_p' + phaseIdx + '/' + Date.now() + '_' + file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          snap => {
            const pct = Math.round(snap.bytesTransferred / snap.totalBytes * 100);
            setSubmitProgress(prev => ({ ...prev, [fileKey]: pct }));
          },
          err => { reject(err); },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            const now = new Date().toISOString();
            await phaseUpdate(tenderId, phaseIdx, phase => {
              const existing = (phase?.submitFiles || []).map(fileMeta);
              const stamp = {
                name: file.name, url, size: file.size,
                uploadedAt: now, uploadedBy: currentUser.uid, uploadedByName: myName,
              };
              if (item.replaceId) {
                // Ny version: den gamla flyttas till historiken och godkännandet
                // nollställs. Annars skulle en avbockad handling kunna bytas ut
                // i tysthet och fortfarande räknas som godkänd.
                return { submitFiles: existing.map(f => f.id !== item.replaceId ? f : {
                  ...f, ...stamp,
                  version: (f.version || 1) + 1,
                  approval: { status: 'pending' },
                  versions: [...(f.versions || []), {
                    version: f.version || 1, name: f.name, url: f.url, size: f.size,
                    uploadedAt: f.uploadedAt, uploadedBy: f.uploadedBy,
                    uploadedByName: f.uploadedByName, approval: f.approval,
                  }],
                }) };
              }
              return { submitFiles: [...existing, {
                ...stamp, id: newFileId(), version: 1,
                approval: { status: 'pending' }, versions: [], contributor,
              }] };
            });
            setSubmitProgress(prev => { const n = { ...prev }; delete n[fileKey]; return n; });
            resolve();
          }
        );
      });
    }
    setSaved(`${items.length} handling${items.length === 1 ? '' : 'ar'} uppladdad`
      + `${items.length === 1 ? '' : 'e'} — väntar på godkännande`);
    setTimeout(() => setSaved(''), 3000);
  }

  // Bara anbudsansvarig, VD, superadmin och admin får bocka av. Alla i teamet
  // får ladda upp och märka avsändare.
  function canApprove() {
    return canOverrideStatus || userProfile?.isAnbudsansvarig === true;
  }

  async function handleToggleApproval(tenderId, phaseIdx, id) {
    if (!canApprove()) return;
    const me = allUsers.find(u => u.id === currentUser.uid);
    const myName = me?.name || me?.displayName || me?.email || 'Okänd';
    await phaseUpdate(tenderId, phaseIdx, phase => ({
      submitFiles: (phase?.submitFiles || []).map(fileMeta).map(f => f.id !== id ? f : {
        ...f,
        approval: f.approval?.status === 'approved'
          ? { status: 'pending' }
          : { status: 'approved', by: currentUser.uid, byName: myName, at: new Date().toISOString() },
      }),
    }));
  }

  async function handleUploadConfirmFile(e, tenderId, fileType, phaseIdx) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const file of files) {
      const fileKey = 'confirm_' + tenderId + '_' + file.name;
      setConfirmProgress(prev => ({ ...prev, [fileKey]: 1 }));
      const storageRef = ref(storage,
        'tenders/' + tenderId + '/confirm_p' + phaseIdx + '/' + Date.now() + '_' + file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          snap => {
            const pct = Math.round(snap.bytesTransferred / snap.totalBytes * 100);
            setConfirmProgress(prev => ({ ...prev, [fileKey]: pct }));
          },
          err => { reject(err); },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            await phaseUpdate(tenderId, phaseIdx, phase => {
              const existing = phase?.confirmFiles || [];
              return { confirmFiles: [...existing, { name: file.name, url, type: fileType, uploadedAt: new Date().toISOString() }] };
            });
            // Status (won/lost/submitted) stays at tender level
            const tenderSnap = await getDoc(doc(db, 'tenders', tenderId));
            if (tenderSnap.data()?.status !== 'submitted') {
              await updateDoc(doc(db, 'tenders', tenderId), { status: 'submitted' });
              setTenders(prev => prev.map(t => t.id === tenderId ? { ...t, status: 'submitted' } : t));
            }
            setConfirmProgress(prev => { const n = { ...prev }; delete n[fileKey]; return n; });
            resolve();
          }
        );
      });
    }
    setSaved('Bekräftelse uppladdad!');
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleUploadEvalFile(e, tenderId) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const file of files) {
      const fileKey = 'eval_' + tenderId + '_' + file.name;
      setEvalProgress(prev => ({ ...prev, [fileKey]: 1 }));
      const storageRef = ref(storage,
        'tenders/' + tenderId + '/eval/' + Date.now() + '_' + file.name);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          snap => {
            const pct = Math.round(snap.bytesTransferred / snap.totalBytes * 100);
            setEvalProgress(prev => ({ ...prev, [fileKey]: pct }));
          },
          err => { reject(err); },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            const tenderSnap = await getDoc(doc(db, 'tenders', tenderId));
            const existing = tenderSnap.data()?.evalFiles || [];
            const newFiles = [...existing, {
              name: file.name, url, uploadedAt: new Date().toISOString()
            }];
            await updateDoc(doc(db, 'tenders', tenderId), { evalFiles: newFiles });
            setEvalProgress(prev => { const n = { ...prev }; delete n[fileKey]; return n; });
            setTenders(prev => prev.map(t =>
              t.id === tenderId ? { ...t, evalFiles: newFiles } : t
            ));
            resolve();
          }
        );
      });
    }
    setSaved('Utvardering uppladdad!');
    setTimeout(() => setSaved(''), 2000);
  }

  async function handleDeleteFile(tenderId, fileUrl, field, phaseIdx) {
    // evalFiles lives at the tender level; all others are phase-specific
    if (field === 'evalFiles') {
      const snap = await getDoc(doc(db, 'tenders', tenderId));
      const newFiles = (snap.data()?.[field] || []).filter(f => f.url !== fileUrl);
      await updateDoc(doc(db, 'tenders', tenderId), { [field]: newFiles });
      setTenders(prev => prev.map(t => t.id === tenderId ? { ...t, [field]: newFiles } : t));
    } else {
      await phaseUpdate(tenderId, phaseIdx, phase => ({
        // Matchar på id när det finns — url pekar bara på senaste versionen.
        [field]: (phase?.[field] || []).filter(f => f.id !== fileUrl && f.url !== fileUrl),
      }));
    }
  }

  // Växlar om en handling är vår att ta fram eller partnerns. Nycklas på texten
  // så markeringen överlever att listan sorteras om eller analyseras på nytt.
  async function handleToggleDocOwner(tenderId, phaseIdx, item) {
    const k = docKey(item);
    await phaseUpdate(tenderId, phaseIdx, phase => {
      const ours = phase?.ourDocuments || [];
      return { ourDocuments: ours.includes(k) ? ours.filter(x => x !== k) : [...ours, k] };
    });
  }

  async function handleUpdateDeadline(tenderId, newDeadline, phaseIdx) {
    await phaseUpdate(tenderId, phaseIdx, () => ({ submissionDeadline: newDeadline }));
  }

  async function analyzeEvaluation(tenderId, phaseIdx = 0) {
    setEvaluating(true);
    setEvaluatingId(tenderId);
    setError('');
    try {
      const tenderSnap = await getDoc(doc(db, 'tenders', tenderId));
      const tenderData = tenderSnap.data();
      const evalFiles = tenderData?.evalFiles || [];
      if (!evalFiles.length) {
        setError('Ladda upp utvärderingsdokument först.');
        return;
      }

      const fileDocs = await Promise.all(evalFiles.map(async file => {
        const response = await fetch(file.url);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        return { name: file.name, base64 };
      }));

      // Modellen måste veta vilken anbudsgivare som är vi – annars kan den
      // inte läsa ut vårt betyg ur ett tilldelningsbeslut.
      let ourCompany = 'TIKAB';
      if (tenderData.companyId) {
        try {
          const c = await getDoc(doc(db, 'companies', tenderData.companyId));
          if (c.exists() && c.data().name) ourCompany = c.data().name;
        } catch { /* behåll standardnamnet */ }
      }
      const bidder = tenderData.bidRole === 'subconsultant' && tenderData.partnerName
        ? `${tenderData.partnerName} (vi, ${ourCompany}, var underkonsult åt dem – deras anbud är "vårt")`
        : `${ourCompany} (eller annat bolag i Tikab-koncernen)`;

      const content = [
        ...fileDocs.map(f => ({
          type: 'document',
          source: { type: 'base64', media_type: 'application/pdf', data: f.base64 },
          title: f.name
        })),
        {
          type: 'text',
          text: `Analysera utvärderingsdokumenten/tilldelningsbeslutet för vårt anbud.

Anbud: "${tenderData.name}"${tenderData.tenderNumber ? ` (vår ref ${tenderData.tenderNumber})` : ''}
Upphandlande kund: "${tenderData.client || 'okänd'}"
Vår anbudsgivare: ${bidder}

Kontrollera först att dokumenten gäller just den här upphandlingen. Gäller de en annan upphandling: sätt documentMatchesTender till false, result till "unknown" och beskriv i documentReference vilken upphandling de gäller.

Fält som inte framgår lämnas som tom sträng eller tom lista. Gissa inte.`
        }
      ];

      const parsed = await callClaudeJSON({
        model: 'claude-opus-5',
        // Rymmer modellens tänkande över flera PDF:er – 2 000 räckte inte och
        // gav ett avhugget svar som inte gick att tolka.
        max_tokens: 16000,
        system: 'Du är expert på offentlig upphandling och anbudsutvärdering och svarar på svenska.',
        messages: [{ role: 'user', content }],
        schema: EVAL_SCHEMA,
      });

      const updates = { evalAnalysis: parsed, evalAnalyzedAt: new Date() };
      await updateDoc(doc(db, 'tenders', tenderId), updates);
      setTenders(prev => prev.map(t =>
        t.id === tenderId ? { ...t, ...updates } : t
      ));

      if (!parsed.documentMatchesTender) {
        setError(`Utvärderingen verkar gälla en annan upphandling (${parsed.documentReference || 'okänd'}). `
          + 'Inget utfall har satts – kontrollera dokumenten.');
        return;
      }
      // AI:n får sätta utfallet bara om ingen har gjort det för hand.
      const phase = tenderData.phases?.[phaseIdx] ?? tenderData;
      if (parsed.result !== 'unknown' && !phase.outcome) {
        await handleSetPhaseOutcome(tenderId, phaseIdx, parsed.result);
      }

      setSaved('Utvardering analyserad!');
      setTimeout(() => setSaved(''), 3000);
    } catch (err) {
      console.error(err);
      // Felet ligger kvar tills det stängs – försvinner det efter några
      // sekunder ser den gamla analysen ut som resultatet av den nya körningen.
      setError(`Kunde inte analysera utvärderingen: ${err?.message || err}. Den tidigare analysen ligger kvar.`);
    } finally {
      setEvaluating(false);
      setEvaluatingId(null);
    }
  }

  const selKey = (tenderId, phaseIdx) => `${tenderId}_${phaseIdx}`;

  // Base64 gör nyttolasten ~33 % större än filen. Gränsen mäts på den kodade
  // storleken, så räkna på samma sätt här som molnfunktionen gör.
  const ANALYSIS_LIMIT_MB = 30;
  const encodedMb = bytes => (bytes * 4 / 3) / (1024 * 1024);

  function fileBytes(file) {
    return file.size ?? fileSizes[file.url] ?? null;
  }

  function selectedFiles(tenderId, phaseIdx, files) {
    const sel = analyzeSel[selKey(tenderId, phaseIdx)];
    return sel ? files.filter(f => sel.includes(f.url)) : files;
  }

  function toggleAnalyzeFile(tenderId, phaseIdx, files, url) {
    const key = selKey(tenderId, phaseIdx);
    setAnalyzeSel(prev => {
      const current = prev[key] ?? files.map(f => f.url);
      return { ...prev, [key]: current.includes(url)
        ? current.filter(u => u !== url)
        : [...current, url] };
    });
  }

  // Hämtar storlek för filer som saknar den. Gamla uppladdningar sparade den
  // inte, så den måste läsas ur Storage en gång.
  const loadFileSizes = useCallback(async (files) => {
    const missing = (files || []).filter(f => f.size == null && fileSizes[f.url] === undefined);
    if (!missing.length) return;
    const found = {};
    await Promise.all(missing.map(async f => {
      try {
        const meta = await getMetadata(ref(storage, f.url));
        found[f.url] = meta.size;
      } catch (_) {
        found[f.url] = null; // okänd storlek — blockera inte analysen för det
      }
    }));
    setFileSizes(prev => ({ ...prev, ...found }));
  }, [fileSizes]);

  // Storleken behövs för mätaren och för att avgöra vad som får plats. Hämtas
  // bara för kort som är utfällda, så listvyn inte drar igång hundra anrop.
  useEffect(() => {
    const open = tenders.filter(t => !collapsed[t.id]);
    const files = open.flatMap(t =>
      ((t.phases?.[activePhaseIdx[t.id] || 0] ?? t)?.briefingFiles) || []);
    if (files.length) loadFileSizes(files);
  }, [tenders, collapsed, activePhaseIdx, loadFileSizes]);

  // Förfrågningsunderlag kommer som PDF eller Word. PDF går rakt in som
  // document-block; .docx plockas ur till text lokalt med mammoth eftersom
  // API:t inte tar emot Word-filer.
  async function briefingFileToContent(file) {
    const response = await fetch(file.url);
    const blob = await response.blob();
    const isPdf = /\.pdf$/i.test(file.name) || blob.type === 'application/pdf';

    if (isPdf) {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return {
        type: 'document',
        source: { type: 'base64', media_type: 'application/pdf', data: base64 },
        title: file.name,
      };
    }

    if (/\.docx?$/i.test(file.name)) {
      const arrayBuffer = await blob.arrayBuffer();
      const { value } = await mammoth.extractRawText({ arrayBuffer });
      return { type: 'text', text: `--- ${file.name} ---
${value}` };
    }

    return null; // filformat vi inte kan läsa — hoppas över, användaren får veta
  }

  async function analyzeBriefing(tenderId, phaseIdx) {
    setAnalyzing(true);
    setAnalyzingId(tenderId);
    setError('');
    // Beskedet hör hemma i kortet, inte i sidans gemensamma ruta högst upp —
    // den hamnar utanför bild och läses aldrig.
    const say = (severity, text) => {
      setAnalysisMsg(prev => ({ ...prev, [tenderId]: { severity, text } }));
      setTimeout(() => document.getElementById('analysbesked-' + tenderId)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    };
    setAnalysisMsg(prev => { const n = { ...prev }; delete n[tenderId]; return n; });
    // Deklareras utanför try — catch-blocket behöver storleken för att kunna
    // förklara ett avbrutet anrop.
    let payloadMb = 0;
    try {
      const tenderSnap = await getDoc(doc(db, 'tenders', tenderId));
      const tenderData = tenderSnap.data();
      const phaseData = tenderData?.phases?.[phaseIdx] ?? tenderData;
      const allFiles = phaseData?.briefingFiles || [];
      const files = selectedFiles(tenderId, phaseIdx, allFiles);
      if (!allFiles.length) { say('error', 'Ladda upp minst ett dokument först.'); return; }
      if (!files.length) {
        say('error', 'Inga filer är valda för analys — kryssa för minst en i listan över förfrågningsunderlag.');
        return;
      }

      const blocks = await Promise.all(files.map(briefingFileToContent));
      const readable = blocks.filter(Boolean);
      const skipped = files.filter((_, i) => !blocks[i]).map(f => f.name);
      if (!readable.length) {
        say('error', 'Inget av dokumenten kunde läsas. Ladda upp förfrågningsunderlaget som PDF eller Word.');
        return;
      }

      // Base64 är ~33 % större än filen. Säg till innan anropet i stället för att
      // låta molnfunktionen dö på minnesbrist och svara ett kryptiskt "internal".
      payloadMb = readable.reduce(
        (n, b) => n + (b.source?.data?.length || b.text?.length || 0), 0) / (1024 * 1024);
      if (payloadMb > ANALYSIS_LIMIT_MB) {
        say('error', `De ${files.length} valda filerna blir ca ${payloadMb.toFixed(0)} MB efter `
          + `kodning och överskrider gränsen på ${ANALYSIS_LIMIT_MB} MB per analys.
`
          + 'Kryssa ur ritningsbilagor och andra tunga dokument i listan över '
          + 'förfrågningsunderlag ovan — de administrativa föreskrifterna är det '
          + 'som innehåller SKA-krav och handlingar.');
        return;
      }

      const content = [
        ...readable,
        {
          type: 'text',
          text: `Analysera dessa upphandlingsdokument (förfrågningsunderlag, anbudsformulär, ramavtalsdokument eller liknande upphandlingsdokument enligt LOU) noggrant och fyll i alla fält i schemat.

Två fält är särskilt viktiga och får aldrig lämnas tomma när underlaget innehåller information:
- briefingText: en kort sammanfattning av vad upphandlingen efterfrågar, så att en läsare direkt förstår vad som ska levereras och på vilka villkor.
- projectOrientation: en kort orientering om det aktuella projektet — vad som ska byggas eller utföras, var, omfattning och tidplan.

Skilj noga på SKA-krav (obligatoriska, anbudet förkastas utan dem) och BÖR-krav (mervärde som ger poäng). Fält som underlaget inte besvarar lämnas som tom sträng respektive tom lista — hitta inte på.`,
        },
      ];

      // Structured outputs i stället för handplockad JSON ur textsvaret: schemat
      // gör fälten obligatoriska och callClaudeJSON säger ifrån om svaret klipps
      // av vid max_tokens i stället för att falla på ett syntaxfel.
      const parsed = await callClaudeJSON({
        model: 'claude-opus-5',
        max_tokens: 32000,
        schema: BRIEFING_SCHEMA,
        messages: [{ role: 'user', content }],
      });

      // Phase-specific fields (per-step content). Analysen körs ofta om när
      // underlaget kompletteras, så kravlistorna slås ihop i stället för att
      // skrivas över — annars försvinner allt avbockat arbete.
      const must  = mergeChecklist(phaseData?.mustRequirements,
        phaseData?.mustRequirementsChecked, parsed.mustRequirements);
      const should = mergeChecklist(phaseData?.shouldRequirements,
        phaseData?.shouldRequirementsChecked, parsed.shouldRequirements);
      const docs  = mergeChecklist(phaseData?.requiredDocuments,
        phaseData?.requiredDocumentsChecked, parsed.requiredDocuments);

      const phaseUpdates = {
        briefingText: parsed.briefingText || phaseData?.briefingText || '',
        projectOrientation: parsed.projectOrientation || phaseData?.projectOrientation || '',
        requiredRoles: parsed.requiredRoles || [],
        requiredSkills: parsed.requiredSkills || [],
        keywords: parsed.keywords || [],
        mustRequirements: must.items,
        mustRequirementsChecked: must.checked,
        shouldRequirements: should.items,
        shouldRequirementsChecked: should.checked,
        requiredDocuments: docs.items,
        requiredDocumentsChecked: docs.checked,
        // Deadline och portal-URL går att rätta för hand — skriv bara dit
        // AI:ns värde när fältet fortfarande är tomt.
        submissionDeadline: phaseData?.submissionDeadline || parsed.submissionDeadline || '',
        portalUrl: phaseData?.portalUrl || parsed.portalUrl || '',
      };
      // Ett svar där varken sammanfattning eller krav kom med betyder att
      // modellen inte fick något läsbart innehåll — säg det rakt ut i stället
      // för att kvittera "klart" på en tom analys.
      if (!parsed.briefingText?.trim() && !(parsed.mustRequirements || []).length
          && !(parsed.requiredDocuments || []).length) {
        say('error', 'AI:n fick inget läsbart innehåll ur dokumenten — inget sparades. '
          + `Analyserade: ${readable.length} av ${files.length} filer.`
          + (skipped.length ? ` Hoppade över: ${skipped.join(', ')}.` : ''));
        return;
      }

      const addedCount = (must.items.length - (phaseData?.mustRequirements?.length || 0))
        + (should.items.length - (phaseData?.shouldRequirements?.length || 0))
        + (docs.items.length - (phaseData?.requiredDocuments?.length || 0));
      const isReanalysis = (phaseData?.mustRequirements?.length || 0)
        + (phaseData?.requiredDocuments?.length || 0) > 0;
      // Tender-level fields (global metadata)
      const tenderUpdates = {
        projectType: parsed.projectType || '',
        duration: parsed.duration || '',
        contractValue: parsed.contractValue || '',
        aiNotes: parsed.notes || '',
        briefingAnalyzedAt: new Date(),
      };

      await phaseUpdate(tenderId, phaseIdx, () => phaseUpdates);
      await updateDoc(doc(db, 'tenders', tenderId), tenderUpdates);
      setTenders(prev => prev.map(t =>
        t.id === tenderId ? { ...t, ...tenderUpdates } : t
      ));

      const skipNote = (skipped.length ? ` Hoppade över ${skipped.join(', ')} — stöds ej.` : '')
        + (allFiles.length > files.length
          ? ` Analyserade ${files.length} av ${allFiles.length} filer.` : '');
      say('success', isReanalysis
        ? `Analysen uppdaterad — ${addedCount} nya poster, avbockningar behållna.${skipNote}`
        : `Förfrågningsunderlaget analyserat av AI!${skipNote}`);
      setTimeout(() => setSaved(''), 4000);
    } catch (err) {
      console.error(err);
      // Utan orsaken går det inte att skilja ett för stort underlag från ett
      // API-fel, och användaren har inget att gå vidare på.
      // Ligger kvar tills den stängs. Ett fel som slocknar efter några sekunder
      // gör att den som testar bara ser "det snurrade och blev klart".
      // "internal" är Firebase generiska kod när molnfunktionen dör utan att
      // hinna svara — nästan alltid minnesbrist på ett för stort underlag.
      const raw = err?.message || '';
      const detail = (raw === 'internal' || err?.code === 'functions/internal')
        ? 'Servern avbröt anropet — underlaget är sannolikt för stort. '
          + `Dokumenten blev ca ${payloadMb.toFixed(1)} MB efter kodning.`
        : raw;
      say('error', 'Kunde inte analysera dokumenten.' + (detail ? `
${detail}` : ''));
    } finally {
      setAnalyzing(false);
      setAnalyzingId(null);
    }
  }

  async function analyzeAllEvaluations() {
    setAggregating(true);
    setError('');
    try {
      const lostTenders = tenders.filter(t =>
        t.status === 'lost' && t.evalAnalysis
      );
      if (!lostTenders.length) {
        setError('Inga utvärderade förlorade anbud hittades.');
        setAggregating(false);
        return;
      }

      const summaries = lostTenders.map(t => ({
        name: t.name,
        client: t.client,
        ourScore: t.evalAnalysis?.ourScore,
        winnerScore: t.evalAnalysis?.winnerScore,
        ourPrice: t.evalAnalysis?.ourPrice,
        winnerPrice: t.evalAnalysis?.winnerPrice,
        priceAnalysis: t.evalAnalysis?.priceAnalysis,
        qualityAnalysis: t.evalAnalysis?.qualityAnalysis,
        mainReasons: t.evalAnalysis?.mainReasons,
        improvements: t.evalAnalysis?.improvements,
        summary: t.evalAnalysis?.summary
      }));

      const data = await callClaude({
        model: 'claude-opus-5',
        max_tokens: 3000,
        messages: [{
          role: 'user',
          content: `Du är expert på offentlig upphandling.

Analysera dessa ${lostTenders.length} förlorade anbud och hitta gemensamma mönster och förbättringsområden:

${JSON.stringify(summaries, null, 2)}

Ge en djupgående analys på svenska med:
1. Är vi för dyra? Visa konkret med siffror om tillgängligt.
2. Hur får vi på kvalitetskriterier jämfört med vinnarna?
3. Vilka mönster ser du i varför vi förlorar?
4. De viktigaste åtgärderna vi bör vidta.
5. En övergripande sammanfattning.

Svara i lopande text med tydliga rubriker. Var konkret och direkt.`
        }]
      });
      setAggregateResult(data.content[0].text);
      setAggregateDialog(true);
    } catch (err) {
      console.error(err);
      setError('Kunde inte analysera utvärderingarna.');
    } finally {
      setAggregating(false);
    }
  }

  // STATISTIK
  const total = tenders.length;
  const won = tenders.filter(t => t.status === 'won').length;
  const lost = tenders.filter(t => t.status === 'lost').length;
  const active = tenders.filter(t =>
    t.status === 'active' || t.status === 'draft' || t.status === 'submitted'
  ).length;
  const decided = won + lost;
  const winRate = decided > 0 ? Math.round((won / decided) * 100) : null;

  const statusLabels = {
    draft: 'Utkast', active: 'Aktivt', submitted: 'Inlämnat',
    won: 'Vunnet', lost: 'Förlorat', declined: 'Avvisat', withdrawn: 'Återdraget'
  };
  const allCollapsed = tenders.length > 0 && tenders.every(t => collapsed[t.id]);

  function changeView(next) {
    setView(next);
    saveView(currentUser?.uid, next);
    // Ett statusfilter som inte finns i den nya vyn skulle bara ge en tom lista.
    if (filterStatus && !statusesForView(next).includes(filterStatus)) setFilterStatus('');
  }

  // Sök, AO och roll – allt utom vy och status. Används både för listan och
  // för antalen i vyväljaren.
  function matchesBaseFilters(t) {
    if (filterDept && t.departmentId !== filterDept) return false;
    if (filterRole && (t.bidRole || 'prime') !== filterRole) return false;
    if (search) {
      const q = search.toLowerCase();
      const dept = departments.find(d => d.id === t.departmentId);
      return !!(
        t.name?.toLowerCase().includes(q) ||
        t.client?.toLowerCase().includes(q) ||
        t.partnerName?.toLowerCase().includes(q) ||
        t.tenderNumber?.toLowerCase().includes(q) ||
        dept?.name?.toLowerCase().includes(q) ||
        dept?.offerCode?.toLowerCase().includes(q)
      );
    }
    return true;
  }
  const baseMatches = tenders.filter(matchesBaseFilters);
  const viewCounts = Object.fromEntries(
    TENDER_VIEWS.map(v => [v.key, baseMatches.filter(t => inView(t, v.key)).length])
  );

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">Anbud</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: 'white', color: '#1a1a1a' }}
            onClick={openNew}>
            Nytt anbud
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
            onClose={() => setError('')}>{error}</Alert>
        )}
        {analyzing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            AI analyserar FU – detta kan ta en stund...
          </Alert>
        )}

        {/* STATISTIKPANEL */}
        {total > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon sx={{ color: '#FF6B00' }} />
                <Typography variant="h6" fontWeight="bold">Anbudsstatistik</Typography>
              </Box>
              {lost > 0 && (
                <Button variant="outlined" size="small"
                  startIcon={aggregating
                    ? <CircularProgress size={14} />
                    : <AutoFixHighIcon />}
                  onClick={analyzeAllEvaluations}
                  disabled={aggregating}
                  sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
                  {aggregating ? 'Analyserar...' : 'AI-analys av alla förluster'}
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <StatBox label="Totalt" value={total} color="#1a1a1a" />
              <StatBox label="Aktiva" value={active} color="#1565c0" />
              <StatBox label="Vunna" value={won} color="#2e7d32"
                onClick={() => changeView('closed')} hint="Visa avslutade" />
              <StatBox label="Förlorade" value={lost} color="#c62828"
                onClick={() => changeView('closed')} hint="Visa avslutade" />
              {winRate !== null && (
                <StatBox
                  label="Vinstprocent"
                  value={winRate + '%'}
                  color={winRate >= 50 ? '#2e7d32' : winRate >= 30 ? '#f57f17' : '#c62828'}
                  large
                />
              )}
            </Box>

            {/* Visuell progress-bar */}
            {decided > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', height: 8, borderRadius: 4,
                  overflow: 'hidden', bgcolor: '#ffebee' }}>
                  <Box sx={{
                    width: winRate + '%', bgcolor: '#2e7d32',
                    transition: 'width 0.5s ease'
                  }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                    {won} vunna
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#c62828' }}>
                    {lost} förlorade
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {Object.keys(uploadProgress).length > 0 && (
          <Box sx={{ mb: 2 }}>
            {Object.entries(uploadProgress).map(([key, pct]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption">
                  {key.split('_').slice(1).join('_')}
                </Typography>
                <LinearProgress variant="determinate" value={pct} />
              </Box>
            ))}
          </Box>
        )}
        {Object.keys(submitProgress).length > 0 && (
          <Box sx={{ mb: 2 }}>
            {Object.entries(submitProgress).map(([key, pct]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption">
                  Laddar upp: {key.split('_').slice(2).join('_')}
                </Typography>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }} />
              </Box>
            ))}
          </Box>
        )}
        {Object.keys(evalProgress).length > 0 && (
          <Box sx={{ mb: 2 }}>
            {Object.entries(evalProgress).map(([key, pct]) => (
              <Box key={key} sx={{ mb: 1 }}>
                <Typography variant="caption">
                  Laddar upp utvärdering: {key.split('_').slice(2).join('_')}
                </Typography>
                <LinearProgress variant="determinate" value={pct}
                  sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#7b1fa2' } }} />
              </Box>
            ))}
          </Box>
        )}

        {/* ── Filter toolbar ── */}
        {tenders.length > 0 && (
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleButtonGroup exclusive size="small" value={view}
              onChange={(e, v) => v && changeView(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.5 },
                '& .Mui-selected': { bgcolor: 'rgba(255,107,0,0.12) !important', color: '#E65100' } }}>
              {TENDER_VIEWS.map(v => (
                <ToggleButton key={v.key} value={v.key}>
                  {v.label}
                  <Box component="span" sx={{ ml: 0.75, fontVariantNumeric: 'tabular-nums', opacity: 0.7 }}>
                    {viewCounts[v.key]}
                  </Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder="Sök anbud, kund..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
              sx={{ width: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Affärsområde</InputLabel>
              <Select value={filterDept} label="Affärsområde"
                onChange={e => setFilterDept(e.target.value)}>
                <MenuItem value="">Alla</MenuItem>
                {departments.filter(d => d.name.toLowerCase() !== 'admin').map(d => (
                  <MenuItem key={d.id} value={d.id}>{deptLabel(d)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status"
                onChange={e => setFilterStatus(e.target.value)}>
                <MenuItem value="">Alla</MenuItem>
                {statusesForView(view).map(st => (
                  <MenuItem key={st} value={st}>{STATUS_PILL[st].label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Vår roll</InputLabel>
              <Select value={filterRole} label="Vår roll"
                onChange={e => setFilterRole(e.target.value)}>
                <MenuItem value="">Alla</MenuItem>
                <MenuItem value="prime">Egna anbud</MenuItem>
                <MenuItem value="subconsultant">Underkonsult</MenuItem>
              </Select>
            </FormControl>
            <Chip
              label="Gruppera per AO"
              size="small"
              onClick={() => setGroupByDept(g => !g)}
              variant={groupByDept ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer', bgcolor: groupByDept ? '#FF6B00' : undefined,
                color: groupByDept ? 'white' : undefined }}
            />
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="outlined"
              onClick={allCollapsed ? expandAll : collapseAll}>
              {allCollapsed ? 'Expandera alla' : 'Minimera alla'}
            </Button>
          </Paper>
        )}

        {tenders.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Inga anbud skapade ännu.</Typography>
            <Button variant="contained" startIcon={<AddIcon />}
              sx={{ mt: 2 }} onClick={openNew}>
              Skapa första anbudet
            </Button>
          </Paper>
        )}

        {(() => {
          if (tenders.length === 0) return null;
          const filtered = baseMatches
            .filter(t => inView(t, view) && (!filterStatus || t.status === filterStatus))
            .sort(byNewestFirst);
          // Avslutade anbud som döljs av vyn Pågående men annars matchar filtren.
          const hiddenClosed = view === 'ongoing' ? baseMatches.filter(isClosed).length : 0;
          const hiddenRow = hiddenClosed > 0 && (
            <Box sx={{ py: 1.5, px: 2, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}>
              {hiddenClosed} avslutade anbud dolda.{' '}
              <Box component="button" type="button" onClick={() => changeView('all')}
                sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: '#E65100',
                  fontWeight: 600, fontSize: 13, '&:hover': { textDecoration: 'underline' } }}>
                Visa
              </Box>
            </Box>
          );

          if (filtered.length === 0) {
            return (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">Inga anbud matchar filtret.</Typography>
                {hiddenRow}
              </Paper>
            );
          }

          const renderItems = items => items.map(tender => {
            const isCollapsed = !!collapsed[tender.id];
            const phaseIdx = getPhaseIdx(tender.id);
            // phase = active phase data; falls back to tender flat fields for legacy tenders
            const phase = tender.phases?.[phaseIdx] ?? tender;
          const mustDone = phase.mustRequirementsChecked?.length || 0;
          const mustTotal = phase.mustRequirements?.length || 0;
          // Som underkonsult är bara en delmängd av handlingarna våra — räkna
          // dem, annars blir siffran missvisande låg för hela upphandlingen.
          const ourDocs = ourDocuments(tender, phase);
          const ourKeys = new Set(ourDocs.map(docKey));
          const checkedIdx = new Set(phase.requiredDocumentsChecked || []);
          const docsTotal = ourDocs.length;
          const docsDone = (phase.requiredDocuments || [])
            .filter((d, i) => checkedIdx.has(i) && ourKeys.has(docKey(d))).length;
          const isDecided = tender.status === 'won' || tender.status === 'lost';
          const closed = isClosed(tender);
          const secCol = key => !!collapsedSections[`${tender.id}_${key}`];
          const togSec = key => setCollapsedSections(prev => ({ ...prev, [`${tender.id}_${key}`]: !prev[`${tender.id}_${key}`] }));
          if (!isCollapsed && teamByTender[tender.id] === undefined) loadTeam(tender.id);
          const members = teamByTender[tender.id] || [];
          const team = members.filter(m => m.status === 'confirmed' && m.type !== 'subcontractor');

          return (
            <Paper key={tender.id} sx={{ mb: 1, overflow: 'hidden' }}>

              {/* RAD – samma kolumner som rubrikraden */}
              <Box onClick={() => toggleCollapse(tender.id)} sx={{
                display: 'grid', gridTemplateColumns: LIST_GRID, columnGap: LIST_GAP,
                alignItems: 'center', px: 2, py: 1.25, cursor: 'pointer',
                borderBottom: isCollapsed ? 'none' : '1px solid', borderColor: 'divider',
                color: closed ? CLOSED_TEXT : 'text.primary',
                bgcolor: isSub(tender) ? 'rgba(0,105,92,0.07)' : undefined,
                '&:hover': { bgcolor: isSub(tender) ? 'rgba(0,105,92,0.12)' : 'action.hover' },
              }}>
                {/* Anbud + roll */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight="bold" noWrap title={tender.name || ''}
                    sx={{ fontSize: 15, color: 'inherit' }}>
                    {tender.name || '–'}
                  </Typography>
                  {(isSub(tender) || tender.evalAnalysis) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      {isSub(tender) && (
                        <Typography noWrap sx={{ fontSize: 12, color: closed ? CLOSED_TEXT : SUBCONSULTANT_COLOR, fontWeight: 600 }}>
                          {tender.partnerName ? `Underkonsult åt ${tender.partnerName}` : 'Underkonsult'}
                        </Typography>
                      )}
                      {tender.evalAnalysis && (
                        <Typography sx={{ fontSize: 11, color: '#7b1fa2', fontWeight: 600, flexShrink: 0 }}>
                          Utvärderad
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Status – klicka för att byta */}
                <Tooltip title="Ändra status">
                  <Box component="button" type="button"
                    onClick={e => { e.stopPropagation(); setStatusMenu({ anchor: e.currentTarget, tender }); }}
                    sx={{
                      width: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: 0, borderRadius: 999, py: 0.25, pl: 1, pr: 0.25, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit', color: 'white',
                      bgcolor: (STATUS_PILL[tender.status] || STATUS_PILL.draft).bg,
                      '&:hover': { filter: 'brightness(0.9)' },
                    }}>
                    {statusLabels[tender.status] || tender.status || '–'}
                    <ArrowDropDownIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>

                {/* Ref */}
                <Typography noWrap sx={{ fontFamily: 'monospace', fontSize: 12, color: 'inherit' }}>
                  {tender.tenderNumber || '–'}
                </Typography>

                {/* AO */}
                {(() => {
                  const d = departments.find(x => x.id === tender.departmentId);
                  return d ? (
                    <Tooltip title={d.name}>
                      <Typography noWrap sx={{ fontSize: 13, color: 'inherit' }}>{deptCode(d)}</Typography>
                    </Tooltip>
                  ) : <Typography sx={{ fontSize: 13 }}>–</Typography>;
                })()}

                {/* Kund */}
                <Typography noWrap title={tender.client || ''} sx={{ fontSize: 13, color: 'inherit' }}>
                  {tender.client || '–'}
                </Typography>

                {/* Sista anbudsdag – som underkonsult gäller vår tid till partnern */}
                {(() => {
                  const useP = isSub(tender) && tender.partnerDeadline;
                  const raw = useP ? tender.partnerDeadline : (phase.submissionDeadline || tender.submissionDeadline);
                  const label = formatDeadline(raw);
                  if (!label) return <Typography sx={{ fontSize: 13 }}>–</Typography>;
                  const rel = closed ? null : relativeDeadline(raw);
                  const tip = useP
                    ? `Vår deadline till ${tender.partnerName || 'partnern'}`
                      + (tender.submissionDeadline ? ` · upphandlingen stänger ${formatDeadline(tender.submissionDeadline)}` : '')
                    : '';
                  const cell = (
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: 13, color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                        {label}
                      </Typography>
                      {rel && (
                        <Typography noWrap sx={{ fontSize: 12, color: DEADLINE_TONE[rel.tone], fontWeight: rel.tone === 'normal' ? 400 : 600 }}>
                          {rel.text}
                        </Typography>
                      )}
                    </Box>
                  );
                  return tip ? <Tooltip title={tip}>{cell}</Tooltip> : cell;
                })()}

                {/* SKA */}
                <Typography sx={{
                  fontSize: 13, fontVariantNumeric: 'tabular-nums',
                  color: closed || !mustTotal ? 'inherit'
                    : mustDone === 0 ? '#C62828' : mustDone === mustTotal ? '#2E7D32' : 'inherit',
                  fontWeight: !closed && mustTotal > 0 && mustDone === 0 ? 700 : 400,
                }}>
                  {closed || !mustTotal ? '–' : `${mustDone}/${mustTotal}`}
                </Typography>

                {/* Dok */}
                <Typography sx={{
                  fontSize: 13, fontVariantNumeric: 'tabular-nums',
                  color: !closed && docsTotal > 0 && docsDone === docsTotal ? '#2E7D32' : 'inherit',
                }}>
                  {closed || !docsTotal ? '–' : `${docsDone}/${docsTotal}`}
                </Typography>

                <IconButton size="small" sx={{ p: 0.5 }}
                  onClick={e => { e.stopPropagation(); toggleCollapse(tender.id); }}>
                  {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                </IconButton>
              </Box>

              {/* INNEHÅLL */}
              <Collapse in={!isCollapsed}>
                <Box sx={{ p: 3 }}>

                  {/* ── Fasbyte ── */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
                    mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                    flexWrap: 'wrap' }}>
                    <Typography variant="caption" fontWeight="bold"
                      color="text.secondary" sx={{ mr: 0.25 }}>
                      FAS:
                    </Typography>
                    {(tender.phases || []).map((ph, idx) => {
                      const outcomeColor = ph.outcome === 'won' ? '#2e7d32' : ph.outcome === 'lost' ? '#c62828' : null;
                      return (
                        <Chip key={ph.id} size="small"
                          label={ph.name + (ph.outcome === 'won' ? ' ✓' : ph.outcome === 'lost' ? ' ✗' : '')}
                          onClick={() => setActivePhaseIdx(prev => ({ ...prev, [tender.id]: idx }))}
                          sx={{
                            cursor: 'pointer', fontWeight: phaseIdx === idx ? 700 : 400,
                            bgcolor: phaseIdx === idx ? (outcomeColor || '#1565C0') : (outcomeColor ? outcomeColor + '22' : 'action.hover'),
                            color: phaseIdx === idx ? 'white' : (outcomeColor || 'text.primary'),
                            border: outcomeColor && phaseIdx !== idx ? '1px solid ' + outcomeColor : 'none',
                          }} />
                      );
                    })}
                    {!tender.phases?.length && (
                      <Chip label="Fas 1" size="small"
                        sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 700 }} />
                    )}
                    <Tooltip title={tender.phases?.length ? 'Lägg till nästa fas' : 'Dela upp i faser'}>
                      <IconButton size="small"
                        onClick={() => handleAddPhase(tender)}
                        sx={{ p: 0.4, border: '1px dashed', borderColor: 'divider',
                          color: 'text.secondary', '&:hover': { borderColor: 'text.secondary' } }}>
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {tenders.length > 1 && (
                      <Button size="small" variant="text"
                        sx={{ ml: 'auto', fontSize: '0.7rem', color: 'text.secondary' }}
                        onClick={() => focusOn(tender.id)}>
                        Fokusera
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>

                      {/* Metadata */}
                      {!isDecided && (
                        <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                          {tender.deadline && (
                            <Typography variant="body2">📅 {tender.deadline}</Typography>
                          )}
                          {phase.submissionDeadline && (
                            <Typography variant="body2">
                              ⏰ Inlamning: {phase.submissionDeadline}
                            </Typography>
                          )}
                          {tender.contractValue && (
                            <Typography variant="body2">
                              💰 {tender.contractValue}
                            </Typography>
                          )}
                          {tender.duration && (
                            <Typography variant="body2">⏱ {tender.duration}</Typography>
                          )}
                        </Box>
                      )}

                      {/* Nedräkningsklocka – bara om ej avgjort */}
                      {!isDecided && (phase.submissionDeadline || tender.deadline) && (
                        <CountdownClock
                          deadline={phase.submissionDeadline || tender.deadline}
                          onUpdateDeadline={newDeadline =>
                            handleUpdateDeadline(tender.id, newDeadline, phaseIdx)}
                        />
                      )}

                      {/* Samarbetspartner — portalen är partnerns ansvar */}
                      {isSub(tender) && (
                        <Box sx={{ mt: 1, mb: 1.5, p: 1.5, borderRadius: 2,
                          bgcolor: 'rgba(0,105,92,0.06)',
                          borderLeft: `3px solid ${SUBCONSULTANT_COLOR}` }}>
                          <Typography variant="caption" fontWeight="bold"
                            sx={{ color: SUBCONSULTANT_COLOR, textTransform: 'uppercase',
                              letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
                            Vi levererar till {tender.partnerName || 'samarbetspartnern'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {[tender.partnerContactName, tender.partnerContactEmail,
                              tender.partnerContactPhone].filter(Boolean).join(' · ')
                              || 'Ingen kontaktperson angiven'}
                          </Typography>
                          {tender.partnerDeadline && (
                            <Typography variant="body2" fontWeight="bold"
                              sx={{ color: SUBCONSULTANT_COLOR, mt: 0.5 }}>
                              Vår deadline: {tender.partnerDeadline.replace('T', ' ')}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Anbudsportal */}
                      {phase.portalUrl && !isSub(tender) && (
                        <Box sx={{ display: 'flex', alignItems: 'center',
                          gap: 1, mt: 1, mb: 1 }}>
                          <Typography variant="caption" fontWeight="bold"
                            color="text.secondary">
                            ANBUDSPORTAL:
                          </Typography>
                          <Typography variant="caption"
                            component="a" href={phase.portalUrl}
                            target="_blank" rel="noopener noreferrer"
                            sx={{ color: 'text.primary', textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' } }}>
                            {phase.portalUrl}
                          </Typography>
                        </Box>
                      )}

                      {/* Sammanfattning */}
                      {phase.briefingText && (
                        <Typography variant="body2" color="text.secondary"
                          sx={{ mb: 2, mt: 1 }}>
                          {phase.briefingText}
                        </Typography>
                      )}

                      {/* Orientering om projektet */}
                      {phase.projectOrientation && (
                        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2,
                          bgcolor: 'action.hover', borderLeft: '3px solid #FF6B00' }}>
                          <Typography variant="caption" fontWeight="bold"
                            sx={{ color: '#FF6B00', textTransform: 'uppercase',
                              letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
                            Om projektet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {phase.projectOrientation}
                          </Typography>
                        </Box>
                      )}

                      {/* Roller & Nyckelord */}
                      {(phase.requiredRoles?.length > 0 || phase.keywords?.length > 0) && (
                        <Box sx={{ mb: 2 }}>
                          {phase.requiredRoles?.length > 0 && (
                            <Box sx={{ mb: 0.5 }}>
                              <Typography variant="caption" fontWeight="bold"
                                color="text.secondary">
                                Efterfrågade roller:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {phase.requiredRoles.map((r, i) => (
                                  <Chip key={i} label={r} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          )}
                          {phase.keywords?.length > 0 && (
                            <Box sx={{ mt: phase.requiredRoles?.length > 0 ? 1 : 0 }}>
                              <Typography variant="caption" fontWeight="bold"
                                color="text.secondary">
                                Nyckelord:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {phase.keywords.map((k, i) => (
                                  <Chip key={i} label={k} size="small"
                                    sx={{ bgcolor: 'action.hover' }} />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Kravlistor – alltid synliga (även för vunna/förlorade anbud) */}
                      {(
                        <>
                          <Box sx={{ mb: 2 }}>
                            <Box onClick={() => togSec('ska')} sx={{ display: 'flex', alignItems: 'center',
                              gap: 1, mb: 0.5, cursor: 'pointer', userSelect: 'none',
                              '&:hover': { opacity: 0.75 } }}>
                              <ExpandMoreIcon sx={{ fontSize: 14, color: '#c62828',
                                transform: secCol('ska') ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.2s', flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight="bold" color="error">
                                SKA-KRAV
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({phase.mustRequirementsChecked?.length || 0}/
                                {phase.mustRequirements?.length || 0} uppfyllda)
                              </Typography>
                            </Box>
                            <Collapse in={!secCol('ska')}>
                              <EditableChecklist
                                items={phase.mustRequirements || []}
                                checkedItems={phase.mustRequirementsChecked}
                                onToggle={i => handleToggleCheck(
                                  tender.id, 'mustRequirements', i, phaseIdx)}
                                onAdd={val => handleAddListItem(
                                  tender.id, 'mustRequirements', val,
                                  phase.mustRequirements, phaseIdx)}
                                onRemove={i => handleRemoveListItem(
                                  tender.id, 'mustRequirements', i,
                                  phase.mustRequirements, phaseIdx)}
                                labelColor="#c62828"
                                emptyLabel="Inga SKA-krav – lagg till manuellt"
                              />
                            </Collapse>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box onClick={() => togSec('bor')} sx={{ display: 'flex', alignItems: 'center',
                              gap: 1, mb: 0.5, cursor: 'pointer', userSelect: 'none',
                              '&:hover': { opacity: 0.75 } }}>
                              <ExpandMoreIcon sx={{ fontSize: 14, color: '#f57f17',
                                transform: secCol('bor') ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.2s', flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight="bold"
                                color="warning.main">
                                BOR-KRAV
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({phase.shouldRequirementsChecked?.length || 0}/
                                {phase.shouldRequirements?.length || 0} uppfyllda)
                              </Typography>
                            </Box>
                            <Collapse in={!secCol('bor')}>
                              <EditableChecklist
                                items={phase.shouldRequirements || []}
                                checkedItems={phase.shouldRequirementsChecked}
                                onToggle={i => handleToggleCheck(
                                  tender.id, 'shouldRequirements', i, phaseIdx)}
                                onAdd={val => handleAddListItem(
                                  tender.id, 'shouldRequirements', val,
                                  phase.shouldRequirements, phaseIdx)}
                                onRemove={i => handleRemoveListItem(
                                  tender.id, 'shouldRequirements', i,
                                  phase.shouldRequirements, phaseIdx)}
                                labelColor="#f57f17"
                                emptyLabel="Inga BOR-krav – lagg till manuellt"
                              />
                            </Collapse>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Box onClick={() => togSec('docs')} sx={{ display: 'flex', alignItems: 'center',
                              gap: 1, mb: 0.5, cursor: 'pointer', userSelect: 'none',
                              '&:hover': { opacity: 0.75 } }}>
                              <ExpandMoreIcon sx={{ fontSize: 14, color: 'text.secondary',
                                transform: secCol('docs') ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.2s', flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight="bold"
                                sx={{ color: isSub(tender) ? SUBCONSULTANT_COLOR : 'text.secondary' }}>
                                {isSub(tender) ? 'HANDLINGAR — VÅRA ATT TA FRAM' : 'HANDLINGAR ATT LÄMNA IN'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({docsDone}/{docsTotal} klara
                                {isSub(tender) && (phase.requiredDocuments?.length || 0) > docsTotal
                                  ? ` av ${phase.requiredDocuments.length} i upphandlingen`
                                  : ''})
                              </Typography>
                            </Box>
                            <Collapse in={!secCol('docs')}>
                              <EditableChecklist
                                items={phase.requiredDocuments || []}
                                checkedItems={phase.requiredDocumentsChecked}
                                onToggle={i => handleToggleCheck(
                                  tender.id, 'requiredDocuments', i, phaseIdx)}
                                onAdd={val => handleAddListItem(
                                  tender.id, 'requiredDocuments', val,
                                  phase.requiredDocuments, phaseIdx)}
                                onRemove={i => handleRemoveListItem(
                                  tender.id, 'requiredDocuments', i,
                                  phase.requiredDocuments, phaseIdx)}
                                ownership={isSub(tender) ? {
                                  isOurs: item => ourKeys.has(docKey(item)),
                                  onToggle: i => handleToggleDocOwner(
                                    tender.id, phaseIdx, (phase.requiredDocuments || [])[i]),
                                } : null}
                                emptyLabel="Inga handlingar – lägg till manuellt"
                              />
                            </Collapse>
                          </Box>

                        </>
                      )}

                      {/* KONSULTER & CV */}
                      {team.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box onClick={() => togSec('team')} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.75 } }}>
                            <ExpandMoreIcon sx={{ fontSize: 14, color: '#FF6B00',
                              transform: secCol('team') ? 'rotate(0deg)' : 'rotate(180deg)',
                              transition: 'transform 0.2s', flexShrink: 0 }} />
                            <BadgeIcon sx={{ fontSize: 15, color: '#FF6B00' }} />
                            <Typography variant="caption" fontWeight="bold"
                              sx={{ color: '#FF6B00' }}>
                              KONSULTER
                            </Typography>
                            <Chip size="small" label={`${team.length} st`}
                              sx={{ height: 16, fontSize: '0.62rem',
                                bgcolor: '#fff3e0', color: '#FF6B00' }} />
                          </Box>
                          <Collapse in={!secCol('team')}>
                          {team.map(member => {
                            const user = allUsers.find(u => u.id === member.userId);
                            const name = user?.name || user?.displayName || user?.email || member.userId;
                            return (
                              <Box key={member.id} sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                py: 0.5, px: 1, mb: 0.5, borderRadius: 1,
                                border: '1px solid', borderColor: 'divider',
                                bgcolor: 'action.hover'
                              }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" fontWeight={600}>
                                    {name}
                                  </Typography>
                                  {member.suggestedRole && (
                                    <Typography variant="caption" color="text.secondary"
                                      sx={{ ml: 0.75 }}>
                                      · {member.suggestedRole}
                                    </Typography>
                                  )}
                                  {member.variantGenerated && (
                                    <Chip label="CV klar" size="small"
                                      sx={{ ml: 0.75, height: 15, fontSize: '0.6rem',
                                        bgcolor: 'rgba(46,125,50,0.15)', color: '#2e7d32' }} />
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                  {member.variantGenerated && (
                                    <Button size="small" variant="text"
                                      startIcon={<PreviewIcon sx={{ fontSize: 13 }} />}
                                      onClick={() => navigate(
                                        `/tenders/${tender.id}/variant/${member.userId}/preview`)}
                                      sx={{ fontSize: '0.62rem', py: 0.2, px: 0.6,
                                        color: '#FF6B00', minWidth: 0 }}>
                                      CV
                                    </Button>
                                  )}
                                  <Button size="small" variant="outlined" component="label"
                                    startIcon={<UploadFileIcon sx={{ fontSize: 12 }} />}
                                    sx={{ fontSize: '0.62rem', py: 0.2, px: 0.6,
                                      borderColor: '#2e7d32', color: '#2e7d32',
                                      '&:hover': { bgcolor: 'rgba(46,125,50,0.06)' } }}>
                                    Ladda upp CV-PDF
                                    <input type="file" hidden accept=".pdf"
                                      onChange={e => handleUploadSubmitFile(e, tender.id, phaseIdx)} />
                                  </Button>
                                </Box>
                              </Box>
                            );
                          })}
                          </Collapse>
                        </Box>
                      )}

                      {/* ANBUDSFORMULÄR */}
                      <Box sx={{ mb: 2 }}>
                        <TenderBidForm
                          tender={tender}
                          phase={phase}
                          phaseIdx={phaseIdx}
                          collapsed={secCol('bidform')}
                          onToggleCollapse={() => togSec('bidform')}
                          onUpdate={updated => setTenders(prev =>
                            prev.map(t => t.id === updated.id ? updated : t)
                          )}
                        />
                      </Box>

                      {/* FRÅGOR OCH SVAR */}
                      <Box sx={{ mb: 2 }}>
                        <TenderQA
                          tender={tender}
                          collapsed={secCol('qa')}
                          onToggleCollapse={() => togSec('qa')}
                          onUpdate={updated => setTenders(prev =>
                            prev.map(t => t.id === updated.id ? updated : t)
                          )}
                        />
                      </Box>

                      {/* E-POSTLARM */}
                      <Box sx={{ mb: 2 }}>
                        <TenderAlarms
                          tender={tender}
                          phase={phase}
                          phaseIdx={phaseIdx}
                          allUsers={allUsers}
                          collapsed={secCol('alarms')}
                          onToggleCollapse={() => togSec('alarms')}
                          onUpdate={updated => setTenders(prev =>
                            prev.map(t => t.id === updated.id ? updated : t)
                          )}
                        />
                      </Box>

                      {/* FU-dokument */}
                      {phase.briefingFiles?.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Box onClick={() => togSec('fu')} sx={{ display: 'flex', alignItems: 'center',
                            gap: 0.75, mb: 0.5, cursor: 'pointer', userSelect: 'none',
                            '&:hover': { opacity: 0.75 } }}>
                            <ExpandMoreIcon sx={{ fontSize: 14,
                              transform: secCol('fu') ? 'rotate(0deg)' : 'rotate(180deg)',
                              transition: 'transform 0.2s', flexShrink: 0, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight="bold"
                              color="text.secondary">
                              FORFRÅGNINGSUNDERLAG
                            </Typography>
                            <Chip label={`${phase.briefingFiles.length} st`} size="small"
                              sx={{ height: 16, fontSize: '0.62rem' }} />
                          </Box>
                          <Collapse in={!secCol('fu')}>
                            {(() => {
                              const fuFiles = phase.briefingFiles;
                              const chosen = selectedFiles(tender.id, phaseIdx, fuFiles);
                              const chosenMb = encodedMb(
                                chosen.reduce((n, f) => n + (fileBytes(f) || 0), 0));
                              const unknown = chosen.some(f => fileBytes(f) == null);
                              const over = chosenMb > ANALYSIS_LIMIT_MB;
                              const allPicked = chosen.length === fuFiles.length;
                              return (
                                <>
                                  <List dense sx={{ mt: 0.5 }}>
                                    {fuFiles.map((file, i) => {
                                      const picked = chosen.includes(file);
                                      const bytes = fileBytes(file);
                                      return (
                                        <ListItem key={i}
                                          sx={{ py: 0.3, px: 0, opacity: picked ? 1 : 0.5 }}>
                                          <Tooltip title={picked
                                            ? 'Ingår i AI-analysen — klicka för att utesluta'
                                            : 'Utesluten ur AI-analysen — klicka för att ta med'}>
                                            <IconButton size="small" sx={{ p: 0.2, mr: 0.5 }}
                                              onClick={() => toggleAnalyzeFile(
                                                tender.id, phaseIdx, fuFiles, file.url)}>
                                              {picked
                                                ? <CheckBoxIcon sx={{ fontSize: 17, color: '#FF6B00' }} />
                                                : <CheckBoxOutlineBlankIcon
                                                    sx={{ fontSize: 17, color: 'text.disabled' }} />}
                                            </IconButton>
                                          </Tooltip>
                                          <InsertDriveFileIcon fontSize="small"
                                            sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                                          <ListItemText primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                              <Typography variant="caption"
                                                component="a" href={file.url}
                                                target="_blank" rel="noopener noreferrer"
                                                sx={{ color: 'text.primary', textDecoration: 'none',
                                                  '&:hover': { textDecoration: 'underline' } }}>
                                                {file.name}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary"
                                                sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
                                                {bytes == null ? '-' : encodedMb(bytes).toFixed(1) + ' MB'}
                                              </Typography>
                                            </Box>
                                          } />
                                          <ListItemSecondaryAction>
                                            <IconButton size="small" edge="end"
                                              onClick={() => handleDeleteFile(
                                                tender.id, file.url, 'briefingFiles', phaseIdx)}>
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      );
                                    })}
                                  </List>
                                  <Box sx={{ mt: 0.5, px: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                      <Typography variant="caption"
                                        sx={{ fontWeight: 700,
                                          color: over ? '#c62828' : 'text.secondary' }}>
                                        Valt för analys: {chosenMb.toFixed(1)} MB av {ANALYSIS_LIMIT_MB} MB
                                        {unknown ? ' (någon fil har okänd storlek)' : ''}
                                      </Typography>
                                      <Box sx={{ flex: 1 }} />
                                      <Typography variant="caption" color="text.secondary"
                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => setAnalyzeSel(prev => ({
                                          ...prev,
                                          [selKey(tender.id, phaseIdx)]:
                                            allPicked ? [] : fuFiles.map(f => f.url),
                                        }))}>
                                        {allPicked ? 'Kryssa ur alla' : 'Välj alla'}
                                      </Typography>
                                    </Box>
                                    <LinearProgress variant="determinate"
                                      value={Math.min(100, chosenMb / ANALYSIS_LIMIT_MB * 100)}
                                      sx={{ height: 5, borderRadius: 3,
                                        '& .MuiLinearProgress-bar': {
                                          bgcolor: over ? '#c62828' : '#2e7d32' } }} />
                                    {over && (
                                      <Typography variant="caption"
                                        sx={{ color: '#c62828', mt: 0.5, display: 'block' }}>
                                        Kryssa ur ritningsbilagor — SKA-krav och handlingar står i de
                                        administrativa föreskrifterna.
                                      </Typography>
                                    )}
                                  </Box>
                                </>
                              );
                            })()}
                          </Collapse>
                        </Box>
                      )}

                      {/* ANBUDSHANDLINGAR – uppladdade + bibliotek */}
                      {!isDecided && (
                        <Box sx={{ mb: 2, p: 2, borderRadius: 2,
                          bgcolor: 'action.hover', border: '1px solid',
                          borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', mb: 1 }}>
                            <Box onClick={() => togSec('submit')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', flex: 1, '&:hover': { opacity: 0.75 } }}>
                              <ExpandMoreIcon sx={{ fontSize: 14, color: '#2e7d32',
                                transform: secCol('submit') ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.2s', flexShrink: 0 }} />
                              <SendIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                              <Typography variant="caption" fontWeight="bold"
                                sx={{ color: '#2e7d32' }}>
                                {isSub(tender)
                                  ? `HANDLINGAR TILL ${(tender.partnerName || 'PARTNERN').toUpperCase()}`
                                  : 'ANBUDSHANDLINGAR ATT SKICKA IN'}
                              </Typography>
                              {(() => {
                                const sf = (phase.submitFiles || []).map(fileMeta);
                                if (!sf.length && !(phase.libraryDocuments?.length)) return null;
                                const done = sf.filter(isApproved).length;
                                const allDone = sf.length > 0 && done === sf.length;
                                return (
                                  <Chip size="small"
                                    label={sf.length
                                      ? (allDone ? 'Klart för anbudsportalen'
                                          : `${done} av ${sf.length} godkända`)
                                      : `${phase.libraryDocuments.length} filer`}
                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: allDone ? 700 : 400,
                                      bgcolor: allDone ? '#2e7d32' : '#e8f5e9',
                                      color: allDone ? 'white' : '#2e7d32' }} />
                                );
                              })()}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" variant="outlined"
                                startIcon={<InsertDriveFileIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: '0.65rem', py: 0.3, px: 1,
                                  borderColor: '#FF6B00', color: '#FF6B00',
                                  '&:hover': { borderColor: '#e55a00', bgcolor: 'rgba(255,107,0,0.06)' } }}
                                onClick={() => setPickerTenderInfo({ tenderId: tender.id, phaseIdx })}>
                                Från bibliotek
                              </Button>
                              <Button size="small" variant="outlined" component="label"
                                startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: '0.65rem', py: 0.3, px: 1,
                                  borderColor: '#2e7d32', color: '#2e7d32',
                                  '&:hover': { borderColor: '#1b5e20', bgcolor: 'rgba(46,125,50,0.06)' } }}>
                                Ladda upp
                                <input type="file" hidden multiple
                                  onChange={e => handleUploadSubmitFile(e, tender.id, phaseIdx)} />
                              </Button>
                            </Box>
                          </Box>
                          {(() => {
                            const sf = (phase.submitFiles || []).map(fileMeta);
                            if (sf.length < 2) return null;
                            const done = sf.filter(isApproved).length;
                            return (
                              <LinearProgress variant="determinate"
                                value={done / sf.length * 100}
                                sx={{ height: 5, borderRadius: 3, mb: 1,
                                  '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }} />
                            );
                          })()}
                          <Collapse in={!secCol('submit')}>

                          {/* Uppladdade filer — grupperade per avsändare */}
                          {(() => {
                            const all = (phase.submitFiles || []).map(fileMeta);
                            if (!all.length) return null;
                            const groups = [];
                            for (const f of all) {
                              const k = contribKey(f.contributor);
                              let g = groups.find(x => x.key === k);
                              if (!g) {
                                g = { key: k, label: contribLabel(f.contributor), files: [] };
                                groups.push(g);
                              }
                              g.files.push(f);
                            }
                            // Våra egna handlingar först, underkonsulterna därefter.
                            groups.sort((a, b) => (a.key === 'internal' ? -1 : b.key === 'internal' ? 1 : 0));
                            const mayApprove = canApprove();

                            return groups.map(g => {
                              const done = g.files.filter(isApproved).length;
                              return (
                              <Box key={g.key} sx={{ mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
                                  mt: 1, mb: 0.25 }}>
                                  <Typography variant="caption" fontWeight="bold"
                                    sx={{ color: g.key === 'internal' ? '#2e7d32' : SUBCONSULTANT_COLOR,
                                      textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                    {g.label}
                                  </Typography>
                                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                  <Typography variant="caption" color="text.secondary"
                                    sx={{ fontSize: '0.65rem' }}>
                                    {done} av {g.files.length} godkända
                                  </Typography>
                                </Box>

                                {g.files.map(file => {
                                  const ok = isApproved(file);
                                  const histOpen = !!openHistory[file.id];
                                  return (
                                  <Box key={file.id} sx={{ py: 0.4, borderRadius: 1,
                                    '&:hover .fil-del': { opacity: 1 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                      <Tooltip title={mayApprove
                                        ? (ok ? 'Godkänd — klicka för att häva' : 'Bocka av som godkänd')
                                        : 'Bara anbudsansvarig kan bocka av'}>
                                        <span>
                                          <IconButton size="small" sx={{ p: 0.2 }}
                                            disabled={!mayApprove}
                                            onClick={() => handleToggleApproval(
                                              tender.id, phaseIdx, file.id)}>
                                            {ok
                                              ? <CheckBoxIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                                              : <CheckBoxOutlineBlankIcon
                                                  sx={{ fontSize: 18, color: 'text.disabled' }} />}
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center',
                                          gap: 0.75, flexWrap: 'wrap' }}>
                                          <Typography variant="caption"
                                            component="a" href={file.url}
                                            target="_blank" rel="noopener noreferrer"
                                            sx={{ color: 'text.primary', textDecoration: 'none',
                                              fontWeight: 500,
                                              '&:hover': { textDecoration: 'underline' } }}>
                                            {file.name}
                                          </Typography>
                                          {file.version > 1 && (
                                            <Chip label={'v' + file.version} size="small"
                                              sx={{ height: 15, fontSize: '0.58rem',
                                                bgcolor: '#1a1a1a', color: 'white' }} />
                                          )}
                                          <Chip size="small"
                                            label={ok ? 'Godkänd' : 'Väntar'}
                                            sx={{ height: 15, fontSize: '0.58rem',
                                              bgcolor: ok ? '#e8f5e9' : '#fff8e1',
                                              color: ok ? '#2e7d32' : '#E65100' }} />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary"
                                          sx={{ fontSize: '0.65rem', display: 'block' }}>
                                          {[file.uploadedByName || 'Okänd uppladdare',
                                            formatStamp(file.uploadedAt)].filter(Boolean).join(' \u00b7 ')}
                                          {ok && file.approval?.byName
                                            ? `  \u2713 ${file.approval.byName} ${formatStamp(file.approval.at)}`
                                            : ''}
                                        </Typography>
                                        {file.version > 1 && !ok && (
                                          <Typography variant="caption"
                                            sx={{ fontSize: '0.65rem', color: '#E65100', display: 'block' }}>
                                            Ny version — tidigare godkannande havt
                                          </Typography>
                                        )}
                                        {file.versions?.length > 0 && (
                                          <Typography variant="caption"
                                            onClick={() => setOpenHistory(prev => ({
                                              ...prev, [file.id]: !prev[file.id] }))}
                                            sx={{ fontSize: '0.65rem', cursor: 'pointer',
                                              color: 'text.secondary', textDecoration: 'underline' }}>
                                            {histOpen ? 'Dölj historik' : `Historik (${file.versions.length})`}
                                          </Typography>
                                        )}
                                        <Collapse in={histOpen}>
                                          <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid',
                                            borderColor: 'divider' }}>
                                            {[...(file.versions || [])].reverse().map(v => (
                                              <Typography key={v.version} variant="caption"
                                                sx={{ fontSize: '0.65rem', display: 'block',
                                                  color: 'text.secondary' }}>
                                                v{v.version} \u00b7{' '}
                                                <Typography variant="caption" component="a" href={v.url}
                                                  target="_blank" rel="noopener noreferrer"
                                                  sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                                  {v.name}
                                                </Typography>
                                                {' '}\u00b7 {v.uploadedByName || 'Okänd'}{' '}
                                                {formatStamp(v.uploadedAt)}
                                                {v.approval?.status === 'approved'
                                                  ? ` \u00b7 var godkänd av ${v.approval.byName || '-'}` : ''}
                                              </Typography>
                                            ))}
                                          </Box>
                                        </Collapse>
                                      </Box>
                                      <IconButton size="small" className="fil-del"
                                        sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.3 }}
                                        onClick={() => handleDeleteFile(
                                          tender.id, file.id, 'submitFiles', phaseIdx)}>
                                        <DeleteIcon sx={{ fontSize: 15 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  );
                                })}
                              </Box>
                              );
                            });
                          })()}

                          {/* Biblioteksdokument */}
                          {phase.libraryDocuments?.map((d, i) => {
                            const days = d.expiresAt
                              ? Math.ceil((new Date(d.expiresAt) - new Date()) / 86400000)
                              : null;
                            return (
                              <ListItem key={'l_' + i} sx={{ py: 0.3, px: 0 }}>
                                <InsertDriveFileIcon fontSize="small"
                                  sx={{ mr: 1, fontSize: 16,
                                    color: days !== null && days < 0 ? 'error.main' : '#FF6B00' }} />
                                <ListItemText primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption"
                                      component="a" href={d.fileUrl}
                                      target="_blank" rel="noopener noreferrer"
                                      sx={{ color: 'text.primary', textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' } }}>
                                      {d.name}
                                    </Typography>
                                    <Chip label={`v${d.version}`} size="small"
                                      sx={{ fontSize: 9, height: 16,
                                        bgcolor: '#1a1a1a', color: 'white' }} />
                                    {days !== null && days < 0 && (
                                      <Chip label="Utgången" size="small" color="error"
                                        sx={{ fontSize: 9, height: 16 }} />
                                    )}
                                    {days !== null && days >= 0 && days <= 30 && (
                                      <Chip label={`${days}d kvar`} size="small" color="warning"
                                        sx={{ fontSize: 9, height: 16 }} />
                                    )}
                                  </Box>
                                } />
                              </ListItem>
                            );
                          })}

                          {!phase.submitFiles?.length && !phase.libraryDocuments?.length && (
                            <Typography variant="caption" color="text.secondary"
                              sx={{ fontStyle: 'italic' }}>
                              Ladda upp egna filer eller välj standarddokument från biblioteket
                            </Typography>
                          )}

                          {/* REFERENSUPPDRAG */}
                          {(() => {
                            const refs = tender.tenderReferenceProjects || [];
                            const allRefsCollapsed = refs.length > 0 &&
                              refs.every(r => collapsedRefs[r.refId]);
                            return (
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid',
                                borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center',
                                  justifyContent: 'space-between', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FolderSpecialIcon sx={{ fontSize: 15, color: '#FF6B00' }} />
                                    <Typography variant="caption" fontWeight="bold"
                                      sx={{ color: '#FF6B00' }}>
                                      REFERENSUPPDRAG
                                    </Typography>
                                    {refs.length > 0 && (
                                      <Chip size="small"
                                        label={`${refs.length} st`}
                                        sx={{ height: 16, fontSize: '0.62rem',
                                          bgcolor: '#fff3e0', color: '#FF6B00' }} />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    {refs.length > 1 && (
                                      <Button size="small" variant="text"
                                        sx={{ fontSize: '0.6rem', color: 'text.secondary',
                                          py: 0.2, minWidth: 0 }}
                                        onClick={() => allRefsCollapsed
                                          ? expandAllRefs(refs)
                                          : collapseAllRefs(refs)}>
                                        {allRefsCollapsed ? 'Expandera alla' : 'Minimera alla'}
                                      </Button>
                                    )}
                                    <Button size="small" variant="outlined"
                                      startIcon={<FolderSpecialIcon sx={{ fontSize: 13 }} />}
                                      sx={{ fontSize: '0.62rem', py: 0.2, px: 0.8,
                                        borderColor: '#FF6B00', color: '#FF6B00',
                                        '&:hover': { borderColor: '#e55a00', bgcolor: 'rgba(255,107,0,0.06)' } }}
                                      onClick={() => setRefPickerTenderId(tender.id)}>
                                      Välj uppdrag
                                    </Button>
                                  </Box>
                                </Box>

                                {refs.length > 0 ? refs.map(rp => {
                                  const isRefCollapsed = !!collapsedRefs[rp.refId];
                                  return (
                                    <Box key={rp.refId} sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      mb: 0.5,
                                      overflow: 'hidden'
                                    }}>
                                      {/* Header row – always visible */}
                                      <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        px: 1, py: 0.5, cursor: 'pointer',
                                        bgcolor: isRefCollapsed ? 'transparent' : 'rgba(255,107,0,0.06)',
                                        '&:hover': { bgcolor: 'rgba(255,107,0,0.1)' }
                                      }}
                                        onClick={() => toggleCollapseRef(rp.refId)}>
                                        {isRefCollapsed
                                          ? <ExpandMoreIcon sx={{ fontSize: 15, color: '#FF6B00', flexShrink: 0 }} />
                                          : <ExpandLessIcon sx={{ fontSize: 15, color: '#FF6B00', flexShrink: 0 }} />}
                                        <FolderSpecialIcon sx={{ fontSize: 13, color: '#FF6B00', flexShrink: 0 }} />
                                        <Typography variant="caption" fontWeight={600}
                                          sx={{ flex: 1, minWidth: 0, overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {rp.name}
                                        </Typography>
                                        {rp.client && (
                                          <Typography variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.62rem', flexShrink: 0 }}>
                                            {rp.client}
                                          </Typography>
                                        )}
                                      </Box>

                                      {/* Expanded content */}
                                      <Collapse in={!isRefCollapsed}>
                                        <Box sx={{ px: 1.5, pb: 1, pt: 0.5,
                                          display: 'flex', alignItems: 'center',
                                          gap: 1, flexWrap: 'wrap' }}>
                                          {rp.role && (
                                            <Typography variant="caption"
                                              color="text.secondary"
                                              sx={{ flex: 1, minWidth: 0 }}>
                                              {rp.role}
                                            </Typography>
                                          )}
                                          <Box sx={{ display: 'flex', gap: 0.5,
                                            ml: 'auto', flexShrink: 0 }}>
                                            {refs.length > 1 && (
                                              <Button size="small" variant="text"
                                                sx={{ fontSize: '0.6rem',
                                                  color: 'text.secondary', py: 0.2,
                                                  minWidth: 0 }}
                                                onClick={() => focusOnRef(rp.refId, refs)}>
                                                Fokusera
                                              </Button>
                                            )}
                                            <Button size="small" variant="text"
                                              sx={{ fontSize: '0.62rem', color: '#FF6B00',
                                                py: 0.2, minWidth: 0, whiteSpace: 'nowrap' }}
                                              onClick={() => navigate(
                                                `/tenders/${tender.id}/reference-variant/${rp.refId}`
                                              )}>
                                              Anpassa variant
                                            </Button>
                                            <IconButton size="small"
                                              onClick={() => handleRemoveRefProject(
                                                tender.id, rp.refId,
                                                tender.tenderReferenceProjects
                                              )}
                                              sx={{ p: 0.3 }}>
                                              <DeleteIcon sx={{ fontSize: 13,
                                                color: 'text.secondary' }} />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                }) : (
                                  <Typography variant="caption" color="text.secondary"
                                    sx={{ fontStyle: 'italic' }}>
                                    Inga referensuppdrag valda
                                  </Typography>
                                )}
                              </Box>
                            );
                          })()}
                          </Collapse>
                        </Box>
                      )}

                      {/* INLÄMNINGSBEKRÄFTELSE */}
                      {!isDecided && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2,
                          bgcolor: phase.confirmFiles?.length > 0 ? '#E8F5E9' : '#FFF8E1',
                          border: '1px solid',
                          borderColor: phase.confirmFiles?.length > 0 ? '#A5D6A7' : '#FFE082' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', mb: 1.5 }}>
                            <Box onClick={() => togSec('confirm')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', flex: 1, '&:hover': { opacity: 0.75 } }}>
                              <ExpandMoreIcon sx={{ fontSize: 14,
                                color: phase.confirmFiles?.length > 0 ? '#2E7D32' : '#F57F17',
                                transform: secCol('confirm') ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.2s', flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight="bold"
                                sx={{ color: phase.confirmFiles?.length > 0 ? '#2E7D32' : '#F57F17' }}>
                                {isSub(tender) ? 'KVITTO PÅ LEVERANS TILL PARTNERN' : 'INLÄMNINGSBEKRÄFTELSE'}
                              </Typography>
                              {!phase.confirmFiles?.length && (
                                <Typography variant="caption" color="#F57F17">
                                  {isSub(tender)
                                    ? '— spara mailet som visar att vi levererat i tid'
                                    : '— krävs för status Inlämnat'}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" variant="outlined" component="label"
                                startIcon={confirmProgress['confirm_' + tender.id + '_pdf']
                                  ? <CircularProgress size={12} />
                                  : <UploadFileIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: '0.65rem', py: 0.3, px: 1 }}>
                                Bekräftelse-PDF
                                <input type="file" hidden accept="application/pdf"
                                  onChange={e => handleUploadConfirmFile(e, tender.id, 'pdf', phaseIdx)} />
                              </Button>
                              <Button size="small" variant="outlined" component="label"
                                startIcon={confirmProgress['confirm_' + tender.id + '_email']
                                  ? <CircularProgress size={12} />
                                  : <SendIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: '0.65rem', py: 0.3, px: 1 }}>
                                Mailbekräftelse
                                <input type="file" hidden accept="application/pdf,image/*,.msg,.eml"
                                  onChange={e => handleUploadConfirmFile(e, tender.id, 'email', phaseIdx)} />
                              </Button>
                            </Box>
                          </Box>
                          <Collapse in={!secCol('confirm')}>
                          {phase.confirmFiles?.map((file, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center',
                              gap: 1, py: 0.3 }}>
                              <CheckBoxIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                              <Typography variant="caption"
                                component="a" href={file.url}
                                target="_blank" rel="noopener noreferrer"
                                sx={{ color: '#2E7D32', textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' } }}>
                                {file.name}
                              </Typography>
                              <Chip size="small"
                                label={file.type === 'pdf' ? 'PDF' : 'Mail'}
                                sx={{ height: 16, fontSize: '0.6rem' }} />
                            </Box>
                          ))}
                          </Collapse>
                        </Box>
                      )}

                      {/* UTVÄRDERINGSSEKTION */}
                      {(() => {
                        const po = phase.outcome;
                        return (
                        <Box sx={{
                          mt: 2, p: 2, borderRadius: 2,
                          bgcolor: po === 'won' ? '#f1f8e9' : po === 'lost' ? '#fce4ec' : '#f3e5f5',
                          border: '1px solid',
                          borderColor: po === 'won' ? '#a5d6a7' : po === 'lost' ? '#f48fb1' : '#ce93d8'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', mb: 1.5 }}>
                            <Box onClick={() => togSec('eval')} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none', flex: 1, '&:hover': { opacity: 0.75 } }}>
                              <ExpandMoreIcon sx={{ fontSize: 14,
                                color: po === 'won' ? '#2e7d32' : po === 'lost' ? '#c62828' : '#7b1fa2',
                                transform: secCol('eval') ? 'rotate(0deg)' : 'rotate(180deg)',
                                transition: 'transform 0.2s', flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight="bold"
                                sx={{ color: po === 'won' ? '#2e7d32' : po === 'lost' ? '#c62828' : '#7b1fa2' }}>
                                UTFALL OCH UTVÄRDERING – {phase.name || 'Fas 1'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" variant="outlined" component="label"
                                startIcon={<UploadFileIcon />}
                                sx={{ fontSize: '0.7rem' }}>
                                Ladda upp utvärdering
                                <input type="file" hidden accept=".pdf"
                                  onChange={e => handleUploadEvalFile(e, tender.id)} />
                              </Button>
                              {tender.evalFiles?.length > 0 && (
                                <Button size="small" variant="outlined"
                                  startIcon={evaluating && evaluatingId === tender.id
                                    ? <CircularProgress size={12} />
                                    : <AutoFixHighIcon />}
                                  onClick={() => analyzeEvaluation(tender.id, phaseIdx)}
                                  disabled={evaluating}
                                  sx={{ fontSize: '0.7rem' }}>
                                  {evaluating && evaluatingId === tender.id
                                    ? 'Analyserar...' : 'AI-analys'}
                                </Button>
                              )}
                            </Box>
                          </Box>
                          <Collapse in={!secCol('eval')}>

                          {/* Sätt utfall per fas */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <Button size="small"
                              variant={po === 'won' ? 'contained' : 'outlined'}
                              startIcon={<EmojiEventsIcon />}
                              onClick={() => handleSetPhaseOutcome(tender.id, phaseIdx, 'won')}
                              sx={{
                                bgcolor: po === 'won' ? '#2e7d32' : undefined,
                                color: po === 'won' ? 'white' : '#2e7d32',
                                borderColor: '#2e7d32',
                                '&:hover': { bgcolor: '#1b5e20', color: 'white' }
                              }}>
                              Vunnet
                            </Button>
                            <Button size="small"
                              variant={po === 'lost' ? 'contained' : 'outlined'}
                              startIcon={<ThumbDownIcon />}
                              onClick={() => handleSetPhaseOutcome(tender.id, phaseIdx, 'lost')}
                              sx={{
                                bgcolor: po === 'lost' ? '#c62828' : undefined,
                                color: po === 'lost' ? 'white' : '#c62828',
                                borderColor: '#c62828',
                                '&:hover': { bgcolor: '#b71c1c', color: 'white' }
                              }}>
                              Förlorat
                            </Button>
                            {po && (
                              <Button size="small" variant="text"
                                onClick={() => handleSetPhaseOutcome(tender.id, phaseIdx, null)}
                                sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                Återställ
                              </Button>
                            )}
                          </Box>

                        {/* Uppladdade utvärderingsfiler */}
                        {tender.evalFiles?.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary"
                              fontWeight="bold">
                              UTVARDERINGSDOKUMENT:
                            </Typography>
                            <List dense>
                              {tender.evalFiles.map((file, i) => (
                                <ListItem key={i} sx={{ py: 0.2, px: 0 }}>
                                  <InsertDriveFileIcon fontSize="small"
                                    sx={{ mr: 1, color: '#7b1fa2', fontSize: 14 }} />
                                  <ListItemText primary={
                                    <Typography variant="caption"
                                      component="a" href={file.url}
                                      target="_blank" rel="noopener noreferrer"
                                      sx={{ color: '#7b1fa2', textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' } }}>
                                      {file.name}
                                    </Typography>
                                  } />
                                  <ListItemSecondaryAction>
                                    <IconButton size="small" edge="end"
                                      onClick={() => handleDeleteFile(
                                        tender.id, file.url, 'evalFiles')}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {/* AI-utvärderingsresultat */}
                        {tender.evalAnalysis && (
                          <Box sx={{ mt: 1 }}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Typography variant="caption" fontWeight="bold"
                              sx={{ color: '#7b1fa2' }}>
                              AI-ANALYS AV UTFALL
                            </Typography>
                            {tender.evalAnalysis.documentMatchesTender === false && (
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                Dokumenten verkar gälla en annan upphandling
                                {tender.evalAnalysis.documentReference ? ` (${tender.evalAnalysis.documentReference})` : ''}.
                                Ta bort fel dokument och kör AI-analysen igen.
                              </Alert>
                            )}
                            {(() => {
                              const analyzed = createdMs({ createdAt: tender.evalAnalyzedAt });
                              const newest = Math.max(0, ...(tender.evalFiles || [])
                                .map(f => Date.parse(f.uploadedAt) || 0));
                              return analyzed && newest > analyzed ? (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  Dokument har laddats upp efter analysen. Kör AI-analysen igen för att uppdatera den.
                                </Alert>
                              ) : null;
                            })()}

                            {(tender.evalAnalysis.ourScore ||
                              tender.evalAnalysis.winnerScore) && (
                              <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1,
                                flexWrap: 'wrap' }}>
                                {tender.evalAnalysis.ourScore && (
                                  <Box sx={{ bgcolor: 'background.paper', p: 1,
                                    borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="caption"
                                      color="text.secondary" display="block">
                                      Vart betyg
                                    </Typography>
                                    <Typography fontWeight="bold" color="#1565c0">
                                      {tender.evalAnalysis.ourScore}
                                    </Typography>
                                  </Box>
                                )}
                                {tender.evalAnalysis.winnerScore && (
                                  <Box sx={{ bgcolor: 'background.paper', p: 1,
                                    borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="caption"
                                      color="text.secondary" display="block">
                                      Vinnarens betyg
                                    </Typography>
                                    <Typography fontWeight="bold" color="#2e7d32">
                                      {tender.evalAnalysis.winnerScore}
                                    </Typography>
                                  </Box>
                                )}
                                {tender.evalAnalysis.ourPrice && (
                                  <Box sx={{ bgcolor: 'background.paper', p: 1,
                                    borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="caption"
                                      color="text.secondary" display="block">
                                      Vart pris
                                    </Typography>
                                    <Typography fontWeight="bold">
                                      {tender.evalAnalysis.ourPrice}
                                    </Typography>
                                  </Box>
                                )}
                                {tender.evalAnalysis.winnerPrice && (
                                  <Box sx={{ bgcolor: 'background.paper', p: 1,
                                    borderRadius: 1, textAlign: 'center' }}>
                                    <Typography variant="caption"
                                      color="text.secondary" display="block">
                                      Vinnarens pris
                                    </Typography>
                                    <Typography fontWeight="bold" color="#2e7d32">
                                      {tender.evalAnalysis.winnerPrice}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {tender.evalAnalysis.summary && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {tender.evalAnalysis.summary}
                              </Typography>
                            )}

                            {tender.evalAnalysis.mainReasons?.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" fontWeight="bold"
                                  color="text.secondary">
                                  Huvudorsaker:
                                </Typography>
                                {tender.evalAnalysis.mainReasons.map((r, i) => (
                                  <Typography key={i} variant="body2"
                                    sx={{ ml: 1 }}>
                                    • {r}
                                  </Typography>
                                ))}
                              </Box>
                            )}

                            {tender.evalAnalysis.improvements?.length > 0 && (
                              <Box>
                                <Typography variant="caption" fontWeight="bold"
                                  color="text.secondary">
                                  Forbattringsomraden:
                                </Typography>
                                {tender.evalAnalysis.improvements.map((r, i) => (
                                  <Typography key={i} variant="body2"
                                    sx={{ ml: 1, color: '#FF6B00' }}>
                                    → {r}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        )}
                          </Collapse>
                      </Box>
                        );
                      })()}

                    </Box>

                    {/* Knappar */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1,
                      ml: 2, minWidth: 180, flexShrink: 0 }}>
                      <Button size="small" variant="contained"
                        startIcon={<PeopleIcon />}
                        onClick={() => navigate('/tenders/' + tender.id + '/team')}
                        sx={{ bgcolor: '#1a1a1a', color: 'white',
                          '&:hover': { bgcolor: '#333' } }}>
                        Valj konsulter
                      </Button>
                      <Button size="small" variant="outlined" component="label"
                        startIcon={<UploadFileIcon />}>
                        Ladda upp FU
                        <input type="file" hidden accept=".pdf" multiple
                          onChange={e => handleUploadFile(e, tender.id, phaseIdx)} />
                      </Button>
                      {phase.briefingFiles?.length > 0 && (
                        <Button size="small" variant="outlined"
                          startIcon={analyzing && analyzingId === tender.id
                            ? <CircularProgress size={14} />
                            : <AutoFixHighIcon />}
                          onClick={() => analyzeBriefing(tender.id, phaseIdx)}
                          disabled={analyzing}>
                          {analyzing && analyzingId === tender.id
                            ? 'Analyserar...' : 'Analysera med AI'}
                        </Button>
                      )}
                      <Button size="small" variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEdit(tender)}>
                        Redigera
                      </Button>
                      <Button size="small" variant="outlined" color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(tender.id)}>
                        Ta bort
                      </Button>
                    </Box>
                  </Box>

                  {/* Analysbesked – här, där knappen är, inte högst upp på sidan */}
                  {analysisMsg[tender.id] && (
                    <Alert id={'analysbesked-' + tender.id}
                      severity={analysisMsg[tender.id].severity}
                      variant="filled"
                      sx={{ mt: 2, whiteSpace: 'pre-wrap', fontWeight: 500,
                        alignItems: 'center' }}
                      onClose={() => setAnalysisMsg(prev => {
                        const n = { ...prev }; delete n[tender.id]; return n;
                      })}>
                      {analysisMsg[tender.id].text}
                    </Alert>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
          }); // closes renderItems

          if (groupByDept) {
            const groups = departments
              .filter(d => d.name.toLowerCase() !== 'admin')
              .map(dept => ({
                dept,
                items: filtered.filter(t => t.departmentId === dept.id)
              }))
              .filter(g => g.items.length > 0);
            const ungrouped = filtered.filter(t => !departments.find(d => d.id === t.departmentId));
            if (ungrouped.length > 0) groups.push({ dept: { name: 'Övrigt', id: '__other__' }, items: ungrouped });

            return renderList(groups.map(({ dept, items }) => (
              <Box key={dept.id} sx={{ mb: 3 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.8, mb: 1, display: 'block' }}>
                  {deptLabel(dept)} ({items.length})
                </Typography>
                {renderItems(items)}
              </Box>
            )));
          }

          return renderList(renderItems(filtered));

          // Rubrikrad + rader i en container som scrollar i sidled på smala skärmar.
          function renderList(content) {
            return (
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: LIST_MIN_WIDTH }}>
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: LIST_GRID, columnGap: LIST_GAP,
                    px: 2, pb: 0.75, fontSize: 12, color: 'text.secondary',
                    textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
                  }}>
                    <span>Anbud</span><span>Status</span><span>Ref</span><span>AO</span>
                    <span>Kund</span><span>Sista anbudsdag</span><span>SKA</span><span>Dok</span><span />
                  </Box>
                  {content}
                  {hiddenRow}
                </Box>
              </Box>
            );
          }
        })()}
      </Container>

      {/* Dialog skapa/redigera */}
      {dialog.open && (
        <Box sx={{
          position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1300
        }}>
          <Paper sx={{ p: 4, width: 520, maxWidth: '90vw',
            maxHeight: '90vh', overflow: 'auto' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {dialog.editId ? 'Redigera anbud' : 'Nytt anbud'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Anbudsnamn" value={form.name} fullWidth
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
              <TextField label="Kund" value={form.client} fullWidth
                onChange={e => setForm(prev => ({ ...prev, client: e.target.value }))} />
              <TextField label="Deadline" value={form.deadline} fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                onChange={e => setForm(prev => ({
                  ...prev, deadline: e.target.value }))} />
              <TextField label="Inlämningsdeadline" value={form.submissionDeadline}
                fullWidth
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                onChange={e => setForm(prev => ({
                  ...prev, submissionDeadline: e.target.value }))} />
              <TextField label="Länk till anbudsportal" value={form.portalUrl}
                fullWidth placeholder="https://..."
                onChange={e => setForm(prev => ({
                  ...prev, portalUrl: e.target.value }))} />
              <TextField label="Affärsområde" select value={form.departmentId}
                fullWidth
                onChange={e => setForm(prev => ({
                  ...prev, departmentId: e.target.value }))}
                SelectProps={{ native: true }}>
                <option value="">Välj affärsområde</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{deptLabel(d)}</option>
                ))}
              </TextField>
              <TextField label="Status" select value={form.status} fullWidth
                onChange={e => setForm(prev => ({
                  ...prev, status: e.target.value }))}
                SelectProps={{ native: true }}>
                <option value="draft">Utkast</option>
                <option value="active">Aktivt</option>
                {(() => {
                  const editing = tenders.find(t => t.id === dialog.editId);
                  const unlocked = canOverrideStatus || hasConfirmation(editing);
                  return (
                    <option value="submitted" disabled={!unlocked}>
                      {unlocked ? 'Inlämnat' : 'Inlämnat 🔒'}
                    </option>
                  );
                })()}
                <option value="won">Vunnet</option>
                <option value="lost">Förlorat</option>
                <option value="declined">Avvisat – lämnas inte in</option>
                <option value="withdrawn">Återdraget</option>
              </TextField>
            </Box>

            {/* ── Vår roll i anbudet ── */}
            <Box sx={{ mt: 2, p: 2, borderRadius: 2,
              bgcolor: form.bidRole === 'subconsultant'
                ? 'rgba(0,105,92,0.06)' : 'action.hover',
              border: '1px solid',
              borderColor: form.bidRole === 'subconsultant'
                ? SUBCONSULTANT_COLOR : 'divider' }}>
              <TextField label="Vår roll" select value={form.bidRole} fullWidth
                onChange={e => setForm(prev => ({ ...prev, bidRole: e.target.value }))}
                SelectProps={{ native: true }}
                helperText={form.bidRole === 'subconsultant'
                  ? 'Partnern lämnar in anbudet — vi tar fram en delmängd av handlingarna'
                  : 'Vi äger anbudet och lämnar in det själva'}>
                {Object.entries(BID_ROLES).map(([key, r]) => (
                  <option key={key} value={key}>{r.label}</option>
                ))}
              </TextField>

              {form.bidRole === 'subconsultant' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField label="Samarbetspartner" select
                      value={form.partnerCompanyId} sx={{ flex: '1 1 220px' }}
                      onChange={e => {
                        const id = e.target.value;
                        const c = crmCompanies.find(x => x.id === id);
                        // Namnet sparas med — partnern ska gå att läsa av på
                        // anbudet även om företaget tas bort ur CRM.
                        setForm(prev => ({ ...prev, partnerCompanyId: id,
                          partnerName: c?.name || prev.partnerName }));
                      }}
                      SelectProps={{ native: true }}
                      helperText="Välj ur CRM, eller lämna tomt och skriv namnet nedan">
                      <option value="">— Finns inte i CRM —</option>
                      {[...crmCompanies].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'))
                        .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </TextField>
                    <TextField label="Partnerns namn" sx={{ flex: '1 1 220px' }}
                      value={form.partnerName}
                      onChange={e => setForm(prev => ({ ...prev, partnerName: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField label="Kontaktperson" sx={{ flex: '1 1 160px' }}
                      value={form.partnerContactName}
                      onChange={e => setForm(prev => ({ ...prev, partnerContactName: e.target.value }))} />
                    <TextField label="E-post" sx={{ flex: '1 1 180px' }}
                      value={form.partnerContactEmail}
                      onChange={e => setForm(prev => ({ ...prev, partnerContactEmail: e.target.value }))} />
                    <TextField label="Telefon" sx={{ flex: '1 1 140px' }}
                      value={form.partnerContactPhone}
                      onChange={e => setForm(prev => ({ ...prev, partnerContactPhone: e.target.value }))} />
                  </Box>
                  <TextField label="Vår deadline till partnern" type="datetime-local"
                    value={form.partnerDeadline}
                    onChange={e => setForm(prev => ({ ...prev, partnerDeadline: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ maxWidth: 280 }}
                    helperText="Ligger normalt före upphandlingens deadline" />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button onClick={() => setDialog({ open: false, editId: '' })}>
                Avbryt
              </Button>
              <Button variant="contained" onClick={handleSave}>Spara</Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Statusmeny från listans statusetikett */}
      <Menu anchorEl={statusMenu?.anchor} open={!!statusMenu} onClose={() => setStatusMenu(null)}>
        {statusMenu && Object.entries(STATUS_PILL).map(([key, pill]) => {
          const locked = key === 'submitted' && !(canOverrideStatus || hasConfirmation(statusMenu.tender));
          return (
            <MenuItem key={key} dense disabled={locked}
              selected={statusMenu.tender.status === key}
              onClick={() => handleQuickStatus(statusMenu.tender, key)}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: pill.bg, mr: 1.5 }} />
              <ListItemText
                primary={pill.label + (locked ? ' 🔒' : '')}
                secondary={STATUS_HINT[key] || (locked ? 'Ladda upp bekräftelse först' : null)}
                primaryTypographyProps={{ fontSize: 14 }}
                secondaryTypographyProps={{ fontSize: 12 }} />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Dialog: vem kommer filerna från, och vad händer med namn som redan finns */}
      <Dialog open={!!uploadQueue} onClose={() => setUploadQueue(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Ladda upp anbudshandling{uploadQueue?.items.length === 1 ? '' : 'ar'}
        </DialogTitle>
        <DialogContent>
          {uploadQueue && (() => {
            const members = teamByTender[uploadQueue.tenderId] || [];
            const needs = members.filter(m => m.type === 'subcontractor');
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <TextField label="Vem kommer handlingen från?" select fullWidth
                  value={uploadQueue.contributorKey}
                  onChange={e => setUploadQueue(q => ({ ...q, contributorKey: e.target.value }))}
                  SelectProps={{ native: true }}
                  helperText={needs.length
                    ? 'Underkonsulterna kommer från anbudets teamsida'
                    : 'Lägg till underkonsultbehov på teamsidan för att kunna märka deras handlingar'}>
                  <option value="internal">Våra handlingar</option>
                  {needs.map(n => (
                    <option key={n.id} value={'sub_' + n.id}>
                      {[n.assignedCompany || n.assignedName, n.suggestedRole]
                        .filter(Boolean).join(' - ') || n.suggestedRole || 'Underkonsult'}
                    </option>
                  ))}
                </TextField>

                {uploadQueue.items.map((item, i) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                    {item.matchName ? (
                      <TextField select fullWidth size="small" sx={{ mt: 1 }}
                        value={item.replaceId ? 'version' : 'separat'}
                        onChange={e => setUploadQueue(q => ({
                          ...q,
                          items: q.items.map((x, j) => j !== i ? x : {
                            ...x, replaceId: e.target.value === 'version' ? x.matchId : '' }),
                        }))}
                        SelectProps={{ native: true }}
                        helperText="Filnamnet finns redan i anbudet">
                        <option value="version">
                          Ny version av {item.matchName} (blir v{(item.matchVersion || 1) + 1})
                        </option>
                        <option value="separat">Separat fil</option>
                      </TextField>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Ny handling
                      </Typography>
                    )}
                  </Box>
                ))}

                <Typography variant="caption" color="text.secondary">
                  Handlingen läggs in som ogodkänd. En ny version nollställer ett
                  tidigare godkännande.
                </Typography>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadQueue(null)}>Avbryt</Button>
          <Button variant="contained" onClick={() => runSubmitUpload(uploadQueue)}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Ladda upp
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aggregerad AI-analys dialog */}
      <Dialog open={aggregateDialog} onClose={() => setAggregateDialog(false)}
        maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#FF6B00' }} />
          AI-analys av alla förlorade anbud
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {aggregateResult}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAggregateDialog(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <DocumentPicker
        open={!!pickerTenderInfo}
        onClose={() => setPickerTenderInfo(null)}
        attachedDocs={(() => {
          const t = tenders.find(x => x.id === pickerTenderInfo?.tenderId);
          return (t?.phases?.[pickerTenderInfo?.phaseIdx] ?? t)?.libraryDocuments || [];
        })()}
        onAttach={docs => handleAttachLibraryDocs(
          pickerTenderInfo.tenderId, docs, pickerTenderInfo.phaseIdx)}
      />

      <ReferencePicker
        open={!!refPickerTenderId}
        onClose={() => setRefPickerTenderId(null)}
        departmentId={tenders.find(t => t.id === refPickerTenderId)?.departmentId}
        attachedRefIds={(tenders.find(t => t.id === refPickerTenderId)?.tenderReferenceProjects || []).map(r => r.refId)}
        onAttach={refs => handleAttachReferenceProjects(refPickerTenderId, refs)}
      />
    </Box>
  );
}

function StatBox({ label, value, color, large, onClick, hint }) {
  const box = (
    <Box onClick={onClick} sx={{
      textAlign: 'center', minWidth: 80,
      ...(onClick ? { cursor: 'pointer', borderRadius: 1, px: 1, '&:hover': { bgcolor: 'action.hover' } } : {}),
    }}>
      <Typography sx={{
        fontWeight: 900,
        fontSize: large ? '2rem' : '1.6rem',
        color, lineHeight: 1
      }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
  return onClick && hint ? <Tooltip title={hint}>{box}</Tooltip> : box;
}